import { C, conventions } from "@bd/content";
import { artRng, Bitmap, hex, INK, mix, shade, type RGB } from "./bitmap";
import { BOXC, furnitureEra } from "./furniture";
import type { Shaft } from "./lighting";
import { drawSmallText, drawText, smallTextWidth, textWidth } from "./pixelfont";
import type { SceneLight } from "./rooms";

export const CW = 480;
export const CH = 270;

export interface CloseupSeat {
  x: number; // top-left where a 26-px character is pasted
  y: number;
  view: "front" | "back";
  /** where speech bubbles point (head) */
  hx: number;
  hy: number;
}

export interface CloseupScene {
  kind: "playtest" | "design" | "launch" | "finale";
  img: Bitmap; // opaque background with furniture
  em: Bitmap; // emissive (window sky, lamp bulb)
  fg: Bitmap; // foreground (drawn after characters, blurred)
  seats: CloseupSeat[];
  designer: CloseupSeat; // where "you" stand
  table?: { cells: [number, number][]; tp: (u: number, v: number) => [number, number] };
  lights: SceneLight[];
  shafts: Shaft[];
  cat: { x: number; y: number };
  /** launch day: where the new box stands on its plinth (top-left, and the size it's drawn at) */
  display?: { x: number; y: number; w: number; h: number };
  /** playtest: the building the room belongs to, and the year it is dressed for (the table follows both) */
  room?: BuildingLevel;
  year?: number;
}

function drawSky(b: Bitmap, emissive: boolean): void {
  for (let y = 20; y < 79; y++) {
    const t = (y - 20) / 58;
    const c = t < 0.7 ? mix([92, 74, 146], [232, 122, 120], t / 0.7) : mix([232, 122, 120], [255, 200, 132], (t - 0.7) / 0.3);
    b.line(38, y, 110, y, c);
  }
  b.ellipse(50, 62, 70, 82, [255, 238, 184]);
  const roof: [number, number][] = [[38, 78], [38, 70], [48, 70], [48, 66], [56, 66], [56, 71], [66, 71], [66, 63], [69, 63], [69, 60], [72, 60], [72, 68], [84, 68], [88, 64], [92, 68], [100, 68], [100, 72], [110, 72], [110, 78]];
  b.poly(roof, [72, 48, 82]);
  for (const [x, y] of [[42, 73], [59, 74], [76, 71], [94, 71], [104, 75]]) b.set(x, y, [255, 212, 120]);
  if (!emissive) {
    b.box(73, 20, 75, 78, [98, 62, 42]);
    b.box(38, 47, 110, 49, [98, 62, 42]);
  } else {
    for (let y = 20; y <= 78; y++) for (let x = 73; x <= 75; x++) b.clear(x, y);
    for (let y = 47; y <= 49; y++) for (let x = 38; x <= 110; x++) b.clear(x, y);
  }
}

function leaf(b: Bitmap, x0: number, y0: number, x1: number, y1: number, wd: number, c: RGB, vein?: RGB): void {
  const ang = Math.atan2(y1 - y0, x1 - x0);
  const nx = -Math.sin(ang);
  const ny = Math.cos(ang);
  const pts: [number, number][] = [];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const r = wd * Math.pow(Math.sin(Math.PI * t), 0.8);
    pts.push([x0 + (x1 - x0) * t + nx * r, y0 + (y1 - y0) * t + ny * r]);
  }
  for (let i = 8; i >= 0; i--) {
    const t = i / 8;
    const r = wd * Math.pow(Math.sin(Math.PI * t), 0.8);
    pts.push([x0 + (x1 - x0) * t - nx * r, y0 + (y1 - y0) * t - ny * r]);
  }
  b.poly(pts, c);
  if (vein) b.line(x0, y0, x1 * 0.85 + x0 * 0.15, y1 * 0.85 + y0 * 0.15, vein);
}

function backWall(sc: Bitmap, wall: RGB, r: () => number): void {
  sc.rect(0, 0, CW, 125, wall);
  for (let y = 12; y < 88; y += 8) for (let x = ((y >> 3) % 2) * 8 + 4; x < CW; x += 16) {
    for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) sc.set(x + dx, y + dy, shade(wall, 0.86));
    sc.set(x, y, shade(wall, 1.2));
  }
  sc.rect(0, 0, CW, 8, [62, 38, 28]);
  sc.line(0, 8, CW, 8, [40, 26, 22]);
  sc.rect(0, 88, CW, 32, [104, 62, 42]);
  sc.rect(0, 88, CW, 4, [138, 88, 58]);
  sc.line(0, 88, CW, 88, [166, 112, 72]);
  for (let x = 6; x < CW; x += 40) {
    sc.strokeBox(x, 95, x + 33, 115, [84, 48, 32]);
    sc.line(x + 1, 115, x + 33, 115, [130, 82, 54]);
  }
  sc.rect(0, 118, CW, 6, [56, 34, 26]);
  void r;
}

function windowAndCurtains(sc: Bitmap, em: Bitmap, curtain: RGB = [140, 46, 58], check = false): void {
  sc.box(34, 16, 114, 82, [96, 60, 40]);
  drawSky(sc, false);
  drawSky(em, true);
  sc.box(30, 82, 118, 86, [132, 86, 56]);
  sc.line(30, 82, 118, 82, [170, 116, 74]);
  sc.line(20, 10, 128, 10, [206, 164, 84]);
  for (const x0 of [22, 110]) {
    sc.box(x0, 11, x0 + 16, 98, curtain);
    if (check) {
      // gingham: pale bands both ways, the crossings paler still
      for (let y = 11; y <= 98; y++) for (let x = x0; x <= x0 + 16; x++) {
        const on = (Math.floor((x - x0) / 3) % 2) + (Math.floor((y - 11) / 3) % 2);
        if (on) sc.set(x, y, mix(curtain, [250, 246, 236], on === 2 ? 0.8 : 0.45));
      }
    } else {
      const red = curtain[0] === 140 && curtain[1] === 46 && curtain[2] === 58;
      for (let fx = x0 + 3; fx < x0 + 16; fx += 4) sc.line(fx, 12, fx, 97, red ? [112, 34, 46] : shade(curtain, 0.8));
      sc.line(x0 + 1, 12, x0 + 1, 97, red ? [170, 70, 80] : mix(curtain, [255, 255, 255], 0.2));
    }
    sc.box(x0, 60, x0 + 16, 62, [206, 164, 84]);
  }
}

function perspectiveFloor(sc: Bitmap, r: () => number, tones: RGB[]): void {
  const vx = 240;
  const vy = -200;
  const fy = 124;
  sc.rect(0, fy, CW, CH - fy, tones[0]);
  const rows = [fy, ...Array.from({ length: 9 }, (_, i) => fy + (CH - fy) * Math.pow((i + 1) / 9, 1.45))];
  const px = (k: number, y: number) => vx + (240 + k * 22 - vx) * ((y - vy) / (CH - vy));
  for (let i = 0; i < 9; i++) {
    const y0 = rows[i];
    const y1 = rows[i + 1];
    for (let k = -30; k < 30; k++) {
      const c = tones[(((k * 7 + i * 3 + ((k * i) % 5)) % 4) + 4) % 4];
      sc.poly([[px(k, y0), y0], [px(k + 1, y0), y0], [px(k + 1, y1), y1], [px(k, y1), y1]], c);
      if ((k + i) % 3 === 0) sc.line(px(k, y0), y0, px(k + 1, y0), y0, shade(tones[0], 0.72));
    }
  }
  for (let k = -30; k <= 30; k++) sc.line(px(k, fy), fy, px(k, CH), CH, shade(tones[0], 0.74));
  for (let i = 0; i < 500; i++) sc.set(Math.floor(r() * CW), fy + Math.floor(r() * (CH - fy)), shade(tones[0], 0.84));
}

function bigPlant(sc: Bitmap): void {
  sc.poly([[12, 118], [40, 118], [37, 140], [15, 140]], [184, 96, 64]);
  sc.box(10, 116, 42, 120, [206, 118, 80]);
  for (const [x0, y0, x1, y1, w, c] of [[26, 116, 4, 70, 8, [46, 104, 66]], [26, 116, 46, 64, 9, [54, 120, 74]], [26, 116, 26, 56, 8, [62, 132, 80]], [26, 116, 10, 90, 7, [60, 128, 78]], [26, 116, 50, 92, 7, [46, 104, 66]], [26, 116, 34, 76, 6, [76, 150, 88]]] as [number, number, number, number, number, RGB][]) {
    leaf(sc, x0, y0, x1, y1, w, c, shade(c, 1.35));
  }
}

function gameShelf(sc: Bitmap, r: () => number): void {
  sc.box(296, 9, 462, 100, [56, 38, 32]);
  for (const sx of [296, 459]) sc.box(sx, 9, sx + 3, 100, [120, 74, 46]);
  const hues: RGB[] = [...BOXC, [110, 70, 140], [210, 90, 130]];
  let trophy = false;
  for (const base of [38, 68, 98]) {
    sc.box(296, base, 462, base + 2, [120, 74, 46]);
    sc.line(296, base, 462, base, [156, 102, 64]);
    let x = 301;
    while (x < 452) {
      if (!trophy && base === 38 && x > 380) {
        sc.box(x + 2, base - 4, x + 8, base - 1, [160, 120, 50]);
        sc.poly([[x + 1, base - 14], [x + 9, base - 14], [x + 7, base - 7], [x + 3, base - 7]], [236, 190, 80]);
        sc.line(x + 2, base - 13, x + 3, base - 9, [255, 236, 160]);
        x += 13;
        trophy = true;
        continue;
      }
      if (r() < 0.18) {
        const bw = 18 + Math.floor(r() * 7);
        const n = 2 + Math.floor(r() * 2);
        for (let k = 0; k < n; k++) {
          const c = hues[Math.floor(r() * hues.length)];
          sc.box(x, base - 5 * (k + 1), x + bw, base - 5 * k - 1, c);
          sc.line(x, base - 5 * (k + 1), x + bw, base - 5 * (k + 1), shade(c, 1.3));
        }
        x += bw + 2;
        continue;
      }
      const bw = 7 + Math.floor(r() * 7);
      const bh = 16 + Math.floor(r() * 10);
      const c = hues[Math.floor(r() * hues.length)];
      sc.box(x, base - bh, x + bw, base - 1, c);
      sc.line(x, base - bh, x, base - 1, shade(c, 1.3));
      sc.line(x + bw, base - bh, x + bw, base - 1, shade(c, 0.7));
      sc.box(x + 2, base - bh + 4, x + bw - 2, base - bh + 6, shade(c, 1.45));
      x += bw + 1;
    }
  }
}

// ------------------------------------------------------------------ the playtest room, by building

/** The company's building: the room the testers sit in follows it. */
export type BuildingLevel = "garage" | "office" | "studio" | "hq";
const LEVELS: BuildingLevel[] = ["garage", "office", "studio", "hq"];

export interface PlaytestRoomOpts {
  /** garage: the kitchen table at home; office: a plain meeting room; studio: the games lab; hq: a proper lab with an observation mirror */
  level?: BuildingLevel;
  seed?: number;
  /** studio only: the wall colour */
  wall?: RGB;
}

const TB = 148;
const TF = 196;
const tableTp = (u: number, v: number): [number, number] => [172 + 136 * u + (154 + 172 * u - (172 + 136 * u)) * v, TB + (TF - TB) * v];
const CREAM: RGB = [244, 238, 222];

function lum(c: RGB): number {
  return 0.3 * c[0] + 0.59 * c[1] + 0.11 * c[2];
}

function hashText(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h;
}

interface RoomOut {
  lights: SceneLight[];
  shafts: Shaft[];
  cat: { x: number; y: number };
}

/**
 * A poster for the era, 48×50 at (x, y): a felt-tip "games night" sheet before there were cons, then
 * the conventions that exist by that year (Dicefall Con and the others in conventions.json), in rotating print styles.
 */
function eraPoster(sc: Bitmap, x: number, y: number, year: number, frame?: RGB): void {
  const cons = conventions.filter((c) => c.year <= year);
  if (frame) sc.box(x - 2, y - 2, x + 50, y + 52, frame);
  const mid = (s: string, small = false) => x + Math.floor((48 - (small ? smallTextWidth(s) : textWidth(s, 0))) / 2);
  if (!cons.length) {
    sc.box(x + 1, y + 1, x + 47, y + 49, [238, 230, 206]);
    sc.line(x + 1, y + 49, x + 47, y + 49, [196, 186, 160]);
    for (const [tx, ty] of [[x, y], [x + 41, y], [x, y + 45], [x + 41, y + 45]]) sc.box(tx, ty, tx + 6, ty + 3, [226, 220, 190]);
    sc.strokeBox(x + 16, y + 7, x + 31, y + 22, [60, 90, 170]);
    sc.line(x + 17, y + 6, x + 32, y + 6, [60, 90, 170]);
    sc.line(x + 32, y + 6, x + 32, y + 21, [60, 90, 170]);
    for (const [px, py] of [[19, 10], [27, 18], [23, 14]]) sc.box(x + px, y + py, x + px + 1, y + py + 1, [200, 60, 60]);
    drawText(sc, mid("GAMES"), y + 28, "GAMES", [200, 60, 60], 0);
    drawText(sc, mid("NIGHT"), y + 37, "NIGHT", [60, 90, 170], 0);
    return;
  }
  const idx = Math.floor(year / 3) % cons.length;
  const words = cons[idx].name.toUpperCase().split(" ");
  const l1 = words[0];
  const l2 = words.slice(1).join(" ");
  const style = (furnitureEra(year) - 1 + idx) % 3;
  const yr = String(year);
  if (style === 0) {
    // the 80s print: navy, a gold rule, a big white die
    sc.box(x, y, x + 48, y + 50, [40, 46, 86]);
    sc.strokeBox(x + 2, y + 2, x + 46, y + 48, [210, 170, 80]);
    sc.box(x + 16, y + 8, x + 31, y + 23, [244, 240, 228]);
    for (const [px, py] of [[19, 11], [28, 11], [23, 15], [19, 20], [28, 20]]) sc.box(x + px, y + py, x + px + 1, y + py + 1, [190, 50, 60]);
    drawText(sc, mid(l1), y + 27, l1, [255, 205, 117], 0);
    if (textWidth(l2, 0) <= 44) drawText(sc, mid(l2), y + 36, l2, [244, 240, 228], 0);
    else drawSmallText(sc, mid(l2, true), y + 37, l2, [244, 240, 228]);
    drawSmallText(sc, mid(yr, true), y + 44, yr, [210, 170, 80]);
  } else if (style === 1) {
    // a loud one: plum with a zigzag band and a big standing meeple
    sc.box(x, y, x + 48, y + 50, [120, 40, 110]);
    for (let k = 0; k < 12; k++) sc.poly([[x + k * 4, y + 26], [x + k * 4 + 2, y + 22], [x + k * 4 + 4, y + 26]], [250, 206, 70]);
    sc.box(x, y + 26, x + 48, y + 27, [250, 206, 70]);
    const mx = x + 24;
    sc.ellipse(mx - 3, y + 4, mx + 3, y + 9, [90, 220, 220]);
    sc.poly([[mx - 10, y + 12], [mx + 10, y + 12], [mx + 5, y + 16], [mx + 7, y + 21], [mx - 7, y + 21], [mx - 5, y + 16]], [90, 220, 220]);
    drawText(sc, mid(l1), y + 29, l1, [255, 246, 226], 0);
    if (textWidth(l2, 0) <= 44) drawText(sc, mid(l2), y + 37, l2, [90, 220, 220], 0);
    else drawSmallText(sc, mid(l2, true), y + 38, l2, [90, 220, 220]);
    drawSmallText(sc, mid(yr, true), y + 45, yr, [250, 206, 70]);
  } else {
    // a modern flat print: cream, a coral sun, an ink die
    sc.box(x, y, x + 48, y + 50, [238, 232, 216]);
    sc.ellipse(x + 10, y + 3, x + 38, y + 25, [236, 112, 92]);
    sc.box(x + 18, y + 9, x + 30, y + 21, [44, 40, 56]);
    for (const [px, py] of [[20, 11], [27, 18], [24, 15]]) sc.box(x + px, y + py, x + px + 1, y + py + 1, [238, 232, 216]);
    drawText(sc, mid(l1), y + 28, l1, [44, 40, 56], 0);
    if (textWidth(l2, 0) <= 44) drawText(sc, mid(l2), y + 37, l2, [214, 90, 72], 0);
    else drawSmallText(sc, mid(l2, true), y + 38, l2, [214, 90, 72]);
    drawSmallText(sc, mid(yr, true), y + 44, yr, [120, 112, 120]);
  }
}

/** A wall calendar, 20×28, showing the year. */
function calendar(sc: Bitmap, x: number, y: number, year: number, pic: RGB): void {
  sc.box(x, y, x + 20, y + 28, [240, 236, 226]);
  sc.line(x, y + 28, x + 20, y + 28, [190, 182, 168]);
  sc.box(x + 2, y + 3, x + 18, y + 11, pic);
  sc.poly([[x + 2, y + 11], [x + 8, y + 6], [x + 13, y + 11]], shade(pic, 0.7));
  sc.ellipse(x + 13, y + 4, x + 16, y + 7, [250, 230, 150]);
  for (let k = 0; k < 4; k++) sc.set(x + 4 + k * 4, y, [80, 80, 90]);
  drawSmallText(sc, x + 3, y + 13, String(year), [190, 60, 60]);
  for (let r = 0; r < 2; r++) for (let c = 0; c < 5; c++) sc.box(x + 2 + c * 4, y + 20 + r * 3, x + 3 + c * 4, y + 21 + r * 3, [150, 146, 150]);
}

function wallClock(sc: Bitmap, cx: number, cy: number, rim: RGB): void {
  sc.ellipse(cx - 9, cy - 9, cx + 9, cy + 9, rim);
  sc.ellipse(cx - 7, cy - 7, cx + 7, cy + 7, [246, 242, 230]);
  for (const [dx, dy] of [[0, -6], [6, 0], [0, 6], [-6, 0]]) sc.set(cx + dx, cy + dy, [70, 60, 70]);
  sc.line(cx, cy, cx, cy - 5, INK);
  sc.line(cx, cy, cx + 3, cy + 2, INK);
}

/** Floor tiles in the same perspective as perspectiveFloor, coloured per tile. */
function tiledFloor(sc: Bitmap, nRows: number, colW: number, col: (k: number, i: number) => RGB, seam?: RGB): void {
  const vx = 240;
  const vy = -200;
  const fy = 124;
  const rows = [fy, ...Array.from({ length: nRows }, (_, i) => fy + (CH - fy) * Math.pow((i + 1) / nRows, 1.45))];
  const px = (k: number, y: number) => vx + (240 + k * colW - vx) * ((y - vy) / (CH - vy));
  const kn = Math.ceil(520 / colW);
  for (let i = 0; i < nRows; i++) for (let k = -kn; k < kn; k++) {
    sc.poly([[px(k, rows[i]), rows[i]], [px(k + 1, rows[i]), rows[i]], [px(k + 1, rows[i + 1]), rows[i + 1]], [px(k, rows[i + 1]), rows[i + 1]]], col(k, i));
  }
  if (seam) {
    for (const y of rows) sc.line(0, Math.round(y), CW, Math.round(y), seam);
    for (let k = -kn; k <= kn; k++) sc.line(px(k, fy), fy, px(k, CH), CH, seam);
  }
}

type ChairKind = "wood" | "chrome" | "office" | "moulded";

function chairs(sc: Bitmap, kind: ChairKind, seat: RGB, leg: RGB): void {
  for (const cx of [206, 276]) {
    if (kind === "office") {
      sc.box(cx - 12, 120, cx + 12, 146, seat);
      sc.line(cx - 12, 120, cx + 12, 120, shade(seat, 1.3));
      sc.box(cx - 10, 122, cx + 10, 124, shade(seat, 1.15));
    } else if (kind === "moulded") {
      sc.ellipse(cx - 13, 120, cx + 13, 136, seat);
      sc.box(cx - 12, 128, cx + 12, 140, seat);
      sc.line(cx - 9, 122, cx + 9, 122, shade(seat, 1.3));
      sc.box(cx - 1, 140, cx + 1, 150, leg);
    } else {
      sc.box(cx - 13, 128, cx - 11, 150, leg);
      sc.box(cx + 11, 128, cx + 13, 150, leg);
      sc.box(cx - 13, 126, cx + 13, 129, seat);
      if (kind === "chrome") sc.box(cx - 11, 131, cx + 11, 135, seat);
    }
  }
  for (const cx of [198, 284]) {
    sc.box(cx - 12, 212, cx + 12, 215, seat);
    sc.line(cx - 12, 212, cx + 12, 212, shade(seat, 1.25));
    if (kind === "office") {
      sc.box(cx - 1, 216, cx + 1, 232, leg);
      sc.line(cx - 12, 234, cx + 12, 234, leg);
      sc.line(cx - 8, 233, cx + 8, 233, shade(leg, 1.2));
      for (const wx of [cx - 12, cx, cx + 12]) sc.box(wx - 1, 235, wx + 1, 237, INK);
    } else if (kind === "moulded") {
      for (const [a, z] of [[cx - 10, cx - 4], [cx + 10, cx + 4]]) sc.line(a, 238, z, 216, leg);
      sc.line(cx - 4, 216, cx + 4, 216, leg);
    } else {
      for (const sx of [cx - 12, cx + 9]) sc.box(sx, 212, sx + 3, 240, leg);
      if (kind === "wood") for (const sx of [cx - 5, cx + 2]) sc.box(sx, 216, sx + 2, 236, shade(leg, 1.07));
      else sc.box(cx - 10, 217, cx + 10, 221, seat);
      sc.box(cx - 12, 228, cx + 12, 230, leg);
    }
  }
}

function sleepingCat(sc: Bitmap, x: number, y: number): void {
  sc.ellipse(x, y, x + 22, y + 9, [232, 150, 72]);
  for (let k = 0; k < 3; k++) sc.box(x + 5 + k * 5, y + 2, x + 6 + k * 5, y + 3, [186, 104, 46]);
  sc.ellipse(x + 15, y - 3, x + 25, y + 6, [236, 158, 80]);
  sc.poly([[x + 16, y - 1], [x + 17, y - 6], [x + 20, y - 2]], [236, 158, 80]);
  sc.poly([[x + 21, y - 2], [x + 24, y - 6], [x + 25, y]], [236, 158, 80]);
  sc.line(x + 18, y + 2, x + 19, y + 2, INK);
  sc.line(x + 22, y + 2, x + 23, y + 2, INK);
  sc.line(x - 3, y + 8, x + 4, y + 8, [232, 150, 72]);
}

function pendantLamp(sc: Bitmap, em: Bitmap, shadeC: RGB, rim: RGB, dome: boolean): void {
  sc.line(240, 8, 240, 40, [40, 30, 30]);
  if (dome) {
    sc.ellipse(226, 34, 254, 54, shadeC);
    sc.box(224, 46, 256, 52, shadeC);
    sc.line(228, 40, 233, 37, shade(shadeC, 1.35));
  } else {
    sc.poly([[232, 40], [248, 40], [258, 52], [222, 52]], shadeC);
    sc.line(232, 41, 223, 51, shade(shadeC, 1.5));
    sc.line(232, 40, 248, 40, rim);
  }
  sc.line(222, 52, 258, 52, rim);
  sc.ellipse(235, 50, 245, 56, [255, 244, 210]);
  em.ellipse(235, 50, 245, 56, [255, 246, 214]);
  em.line(224, 53, 256, 53, [255, 214, 150], 140);
}

function tableShadow(sc: Bitmap): void {
  sc.ellipse(150, 196, 330, 220, [20, 10, 20], 110);
}

function leavesFg(fg: Bitmap, n = 4): void {
  const L: [number, number, number, number, number, RGB][] = [[-10, 280, 60, 196, 16, [30, 70, 46]], [20, 290, 110, 222, 14, [36, 82, 52]], [-20, 250, 40, 176, 11, [40, 92, 58]], [40, 290, 88, 250, 10, [28, 64, 42]]];
  for (const [x0, y0, x1, y1, w, c] of L.slice(0, n)) leaf(fg, x0, y0, x1, y1, w, c, shade(c, 1.4));
}

function protoBoxFg(fg: Bitmap): void {
  fg.box(398, 236, 480, 270, [176, 132, 84]);
  fg.line(398, 236, 480, 236, [206, 164, 110]);
  fg.poly([[398, 236], [410, 226], [440, 226], [432, 236]], [196, 152, 100]);
  fg.box(432, 236, 446, 270, [214, 196, 150]);
  fg.box(404, 228, 424, 236, BOXC[0]);
  fg.box(450, 230, 476, 236, BOXC[6]);
}

/** The games lab (the studio): wood panelling, a rug, the games shelf, the cat asleep in a box. */
function studioRoom(sc: Bitmap, em: Bitmap, fg: Bitmap, year: number, r: () => number, wall: RGB): RoomOut {
  backWall(sc, wall, r);
  windowAndCurtains(sc, em);
  sc.line(134, 8, 134, 16, [70, 56, 44]);
  sc.box(128, 16, 140, 24, [184, 96, 64]);
  for (const [vx, ln] of [[129, 24], [133, 34], [138, 18], [141, 28]]) for (let i = 0; i < ln; i++) {
    const x = vx + Math.round(2 * Math.sin(i / 3 + vx));
    sc.set(x, 24 + i, [52, 112, 70]);
    if (i % 4 === 1) sc.box(x - 1, 24 + i, x + 1, 25 + i, [76, 146, 84]);
  }
  eraPoster(sc, 146, 22, year);
  sc.box(204, 24, 266, 70, [120, 80, 50]);
  sc.box(206, 26, 264, 68, [186, 138, 88]);
  for (let i = 0; i < 90; i++) sc.set(206 + Math.floor(r() * 58), 26 + Math.floor(r() * 42), [160, 114, 70]);
  const cards: [number, number, RGB][] = [[210, 30, [244, 240, 224]], [226, 34, [250, 222, 120]], [244, 29, [244, 240, 224]], [214, 50, [160, 210, 236]], [234, 52, [244, 240, 224]], [252, 46, [244, 180, 170]]];
  for (const [cx, cy, c] of cards) {
    sc.box(cx, cy, cx + 9, cy + 11, c);
    sc.line(cx + 2, cy + 4, cx + 7, cy + 4, [120, 110, 110]);
    sc.line(cx + 2, cy + 7, cx + 6, cy + 7, [120, 110, 110]);
  }
  const pins: [number, number][] = [[214, 31], [230, 35], [248, 30], [256, 47], [238, 53], [218, 51]];
  for (let i = 0; i + 1 < pins.length; i++) sc.line(pins[i][0], pins[i][1], pins[i + 1][0], pins[i + 1][1], [200, 50, 60]);
  for (const [x, y] of pins) sc.set(x, y, [230, 60, 60]);
  calendar(sc, 271, 30, year, [90, 150, 200]);
  gameShelf(sc, r);
  perspectiveFloor(sc, r, [[146, 96, 60], [138, 90, 56], [154, 102, 64], [130, 84, 52]]);
  sc.ellipse(112, 142, 368, 230, [168, 124, 66]);
  sc.ellipse(116, 145, 364, 227, [118, 36, 50]);
  for (let a = 0; a < 360; a += 12) {
    const rad = (a * Math.PI) / 180;
    sc.set(240 + 104 * Math.cos(rad), 186 + 31 * Math.sin(rad), [214, 170, 90]);
  }
  sc.ellipse(150, 160, 330, 212, [132, 44, 58]);
  tableShadow(sc);
  sc.ellipse(66, 197, 96, 203, [20, 10, 20], 120);
  sc.ellipse(398, 226, 446, 234, [20, 10, 20], 120);
  bigPlant(sc);
  chairs(sc, "wood", [132, 82, 50], [110, 66, 40]);
  pendantLamp(sc, em, [46, 112, 84], [214, 172, 92], false);
  // the cat asleep in a game box
  const bx = 404;
  const by = 214;
  sc.poly([[bx, by], [bx + 34, by], [bx + 38, by - 6], [bx + 4, by - 6]], [36, 28, 40]);
  sc.poly([[bx, by], [bx + 4, by - 6], [bx - 6, by - 11], [bx - 9, by - 5]], [96, 140, 210]);
  sc.poly([[bx + 34, by], [bx + 38, by - 6], [bx + 46, by - 9], [bx + 42, by - 3]], [76, 120, 190]);
  sc.ellipse(bx + 5, by - 12, bx + 31, by + 1, [232, 150, 72]);
  for (let k = 0; k < 3; k++) for (let a = 0; a < 6; a++) sc.set(bx + 10 + k * 6 + (a % 3), by - 10 + Math.floor(a / 3), [186, 104, 46]);
  sc.ellipse(bx + 24, by - 15, bx + 35, by - 5, [236, 158, 80]);
  sc.poly([[bx + 25, by - 13], [bx + 26, by - 19], [bx + 29, by - 14]], [236, 158, 80]);
  sc.poly([[bx + 30, by - 14], [bx + 33, by - 19], [bx + 35, by - 12]], [236, 158, 80]);
  sc.set(bx + 26, by - 16, [240, 140, 150]);
  sc.set(bx + 33, by - 16, [240, 140, 150]);
  sc.line(bx + 27, by - 10, bx + 29, by - 10, INK);
  sc.line(bx + 31, by - 10, bx + 33, by - 10, INK);
  sc.box(bx, by, bx + 34, by + 15, [70, 110, 180]);
  sc.line(bx, by, bx + 34, by, [120, 160, 220]);
  sc.box(bx + 6, by + 4, bx + 28, by + 9, [250, 210, 90]);
  drawText(sc, bx + 7, by + 4, "DICE", [60, 40, 50], 0);
  sc.poly([[bx + 34, by], [bx + 38, by - 6], [bx + 38, by + 9], [bx + 34, by + 15]], [50, 80, 140]);
  leavesFg(fg);
  fg.box(396, 238, 480, 270, [112, 72, 46]);
  for (const yy of [246, 256]) fg.line(396, yy, 480, yy, [80, 50, 32]);
  fg.box(404, 226, 424, 238, BOXC[0]);
  fg.box(428, 222, 452, 238, BOXC[6]);
  fg.box(456, 230, 480, 238, BOXC[2]);
  return {
    lights: [
      { px: 240, py: 172, color: [0.8, 0.62, 0.4], radius: 230, strength: 1.4 },
      { px: 240, py: 58, color: [0.9, 0.7, 0.45], radius: 70, strength: 1.2 },
      { px: 75, py: 50, color: [0.62, 0.42, 0.34], radius: 95, strength: 1.0 },
    ],
    shafts: [{ pts: [[40, 22], [108, 22], [250, 270], [112, 270]] }, { pts: [[224, 52], [256, 52], [306, 178], [174, 178]] }],
    cat: { x: bx + 18, y: by - 10 },
  };
}

/** Home: the kitchen table, lino, the fridge covered in drawings, family clutter. Decorated for the decade. */
function kitchenRoom(sc: Bitmap, em: Bitmap, fg: Bitmap, year: number, r: () => number): RoomOut {
  const e = furnitureEra(year);
  const K = ([
    { wall: [214, 178, 96], fleck: [196, 104, 62], lino: [[230, 224, 206], [96, 120, 110]], fridge: [178, 214, 198], cab: [226, 212, 176], curtain: [206, 70, 64], lamp: [222, 116, 54], seat: [206, 70, 64], leg: [184, 188, 196], chair: "chrome" },
    { wall: [198, 146, 92], fleck: [176, 116, 68], lino: [[206, 176, 124], [150, 108, 70]], fridge: [214, 178, 84], cab: [150, 98, 58], curtain: [222, 140, 60], lamp: [150, 92, 52], seat: [214, 150, 70], leg: [184, 188, 196], chair: "chrome" },
    { wall: [178, 198, 212], fleck: [156, 180, 200], lino: [[226, 222, 206], [118, 148, 190]], fridge: [236, 236, 230], cab: [232, 230, 218], curtain: [90, 130, 200], lamp: [240, 236, 220], seat: [176, 124, 80], leg: [150, 104, 64], chair: "wood" },
    { wall: [172, 188, 164], fleck: [164, 180, 156], lino: [[214, 212, 204], [176, 178, 176]], fridge: [188, 194, 200], cab: [86, 102, 120], curtain: [224, 216, 198], lamp: [46, 46, 54], seat: [232, 230, 222], leg: [206, 204, 196], chair: "wood" },
  ] as const)[e];
  // ceiling, wallpaper of the decade, skirting
  sc.rect(0, 0, CW, 124, K.wall);
  for (let y = 12; y < 118; y += 9) for (let x = ((y / 9) % 2) * 9 + 3; x < CW; x += 18) {
    if (e === 0) for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) sc.set(x + dx, y + dy, K.fleck);
    else if (e === 2) sc.set(x, y, K.fleck);
  }
  if (e === 1) for (let x = 0; x < CW; x += 12) sc.box(x, 6, x + 4, 118, K.fleck);
  if (e === 3) for (let i = 0; i < 300; i++) sc.set(Math.floor(r() * CW), 6 + Math.floor(r() * 112), K.fleck);
  sc.rect(0, 0, CW, 6, [236, 230, 218]);
  sc.line(0, 6, CW, 6, [190, 182, 170]);
  sc.rect(0, 118, CW, 6, shade(K.cab, 0.7));
  windowAndCurtains(sc, em, K.curtain, e <= 2);
  // herbs on the sill
  sc.box(88, 76, 98, 82, [184, 96, 64]);
  for (const [lx, ly] of [[90, 72], [93, 69], [96, 72], [92, 74], [95, 75]]) sc.box(lx - 1, ly - 1, lx + 1, ly + 1, [70, 140, 80]);
  wallClock(sc, 141, 30, e === 3 ? [46, 46, 54] : [190, 60, 60]);
  // the poster, taped up
  eraPoster(sc, 158, 18, year);
  for (const [tx, ty] of [[156, 16], [202, 16]]) sc.box(tx, ty, tx + 6, ty + 3, [236, 232, 200]);
  calendar(sc, 240, 16, year, [120, 170, 90]);
  // family photos
  for (const [fx, fy, c] of [[214, 64, [120, 160, 200]], [226, 72, [220, 170, 120]], [134, 50, [200, 150, 190]]] as [number, number, RGB][]) {
    sc.box(fx, fy, fx + 8, fy + 9, [150, 110, 70]);
    sc.box(fx + 1, fy + 1, fx + 7, fy + 8, c);
    sc.ellipse(fx + 2, fy + 3, fx + 5, fy + 6, [230, 190, 150]);
  }
  // cupboards, the tiled splashback, the counter and what lives on it
  sc.box(266, 10, 400, 50, K.cab);
  for (let k = 0; k < 4; k++) {
    sc.strokeBox(268 + k * 33, 12, 297 + k * 33, 48, shade(K.cab, 0.78));
    sc.box(k % 2 ? 270 + k * 33 : 293 + k * 33, 40, k % 2 ? 271 + k * 33 : 294 + k * 33, 43, [200, 200, 206]);
  }
  sc.line(266, 51, 400, 51, shade(K.wall, 0.7));
  sc.box(266, 52, 400, 86, [236, 236, 228]);
  for (let y = 52; y <= 86; y += 6) sc.line(266, y, 400, y, [206, 206, 200]);
  for (let x = 266; x <= 400; x += 6) sc.line(x, 52, x, 86, [206, 206, 200]);
  sc.box(264, 86, 404, 91, e === 1 ? [168, 120, 76] : [180, 176, 168]);
  sc.line(264, 86, 404, 86, e === 1 ? [200, 150, 100] : [214, 212, 206]);
  sc.box(266, 92, 402, 120, K.cab);
  for (let k = 0; k < 4; k++) {
    sc.strokeBox(268 + k * 33, 94, 297 + k * 33, 118, shade(K.cab, 0.78));
    sc.box(280 + k * 33, 96, 285 + k * 33, 97, [200, 200, 206]);
  }
  sc.box(318, 86, 350, 87, [130, 134, 140]);
  sc.line(334, 74, 334, 86, [190, 194, 200]);
  sc.line(334, 74, 341, 74, [190, 194, 200]);
  sc.line(341, 74, 341, 77, [190, 194, 200]);
  // kettle (a stove-top one early on, an electric jug later), toaster, fruit bowl, a cereal box
  if (e <= 1) {
    sc.ellipse(280, 74, 296, 86, [214, 76, 62]);
    sc.box(286, 70, 290, 73, INK);
    sc.line(294, 78, 299, 74, [214, 76, 62]);
  } else {
    sc.box(282, 70, 294, 85, e === 3 ? [190, 196, 204] : [236, 236, 230]);
    sc.box(294, 73, 297, 81, shade(e === 3 ? [190, 196, 204] : [236, 236, 230], 0.8));
    sc.box(285, 79, 288, 80, [80, 200, 240]);
    em.box(285, 79, 288, 80, [80, 200, 240]);
  }
  sc.box(300, 81, 316, 85, [236, 230, 214]);
  for (const [fx, c] of [[302, [214, 60, 50]], [306, [240, 160, 50]], [310, [240, 214, 70]], [304, [100, 170, 70]]] as [number, RGB][]) sc.ellipse(fx, 77, fx + 4, 81, c);
  sc.box(358, 76, 376, 85, [196, 200, 208]);
  sc.line(358, 76, 376, 76, [236, 240, 244]);
  sc.box(362, 74, 366, 75, INK);
  sc.box(368, 74, 372, 75, INK);
  const cer = BOXC[Math.floor(r() * 6)];
  sc.box(382, 62, 394, 85, cer);
  sc.box(384, 66, 392, 70, CREAM);
  sc.ellipse(384, 74, 392, 80, shade(cer, 1.3));
  // the fridge, covered in drawings and magnets
  const fr = K.fridge;
  if (e === 0) {
    sc.ellipse(408, 10, 468, 34, fr);
    sc.box(408, 20, 468, 124, fr);
    sc.line(414, 44, 414, 74, [200, 204, 212]);
    sc.line(415, 44, 415, 74, [240, 242, 246]);
    sc.box(426, 24, 450, 26, [196, 200, 208]);
  } else {
    sc.box(408, 12, 468, 124, fr);
    sc.line(408, 46, 468, 46, shade(fr, 0.72));
    sc.box(412, 26, 413, 42, shade(fr, 0.7));
    sc.box(412, 52, 413, 80, shade(fr, 0.7));
  }
  sc.line(468, 14, 468, 124, shade(fr, 0.8));
  sc.box(408, 120, 468, 124, [60, 60, 66]);
  sc.box(434, 58, 452, 76, [250, 250, 244]);
  sc.ellipse(436, 60, 441, 65, [250, 200, 60]);
  sc.poly([[440, 74], [446, 67], [452, 74]], [214, 70, 60]);
  sc.box(442, 70, 450, 75, [80, 130, 210]);
  sc.line(434, 76, 452, 76, [90, 170, 90]);
  sc.box(418, 84, 430, 94, [160, 200, 230]);
  sc.ellipse(421, 87, 425, 91, [230, 186, 150]);
  sc.box(438, 84, 448, 102, [250, 248, 236]);
  for (let k = 0; k < 4; k++) sc.line(440, 87 + k * 4, 446 - (k % 2) * 2, 87 + k * 4, [110, 110, 140]);
  for (const [mx, my, c] of [[443, 57, [220, 60, 60]], [423, 82, [60, 120, 220]], [442, 82, [240, 200, 60]], [426, 60, [70, 170, 100]], [454, 64, [160, 90, 190]]] as [number, number, RGB][]) sc.box(mx, my, mx + 2, my + 2, c);
  sleepingCat(sc, 426, 3);
  // lino
  tiledFloor(sc, 11, 17, (k, i) => (((k + i) % 2) + 2) % 2 ? mix(K.lino[1], K.lino[0], 0.4) : K.lino[0]);
  tableShadow(sc);
  sc.ellipse(66, 197, 96, 203, [20, 10, 20], 120);
  // clutter: a laundry basket, wellies by the fridge, a ball, a tower of toy blocks, the pet's bowl
  sc.ellipse(14, 224, 58, 234, [20, 10, 20], 110);
  sc.poly([[16, 206], [56, 206], [52, 230], [20, 230]], [214, 188, 136]);
  for (let y = 210; y < 230; y += 4) sc.line(18, y, 54, y, [184, 156, 104]);
  for (const [cx, cy, c] of [[24, 202, [90, 130, 200]], [36, 200, [240, 240, 236]], [46, 203, [230, 150, 170]], [30, 205, [120, 170, 110]]] as [number, number, RGB][]) sc.ellipse(cx - 7, cy - 4, cx + 7, cy + 5, c);
  for (const [bx, c] of [[440, [214, 60, 56]], [452, [214, 60, 56]]] as [number, RGB][]) {
    sc.box(bx, 128, bx + 7, 144, c);
    sc.box(bx, 140, bx + 11, 145, c);
    sc.line(bx, 128, bx + 7, 128, shade(c, 1.35));
  }
  sc.ellipse(386, 142, 402, 148, [70, 120, 190]);
  sc.ellipse(388, 141, 400, 144, [150, 110, 70]);
  sc.ellipse(118, 234, 132, 248, [214, 60, 56]);
  sc.line(118, 241, 132, 241, CREAM);
  sc.ellipse(121, 236, 125, 239, [255, 160, 150]);
  for (const [bx, by, c] of [[350, 228, BOXC[1]], [362, 228, BOXC[2]], [356, 220, BOXC[3]]] as [number, number, RGB][]) {
    sc.box(bx, by, bx + 10, by + 8, c);
    sc.line(bx, by, bx + 10, by, shade(c, 1.35));
  }
  chairs(sc, K.chair, K.seat, K.leg);
  pendantLamp(sc, em, K.lamp, shade(K.lamp, 1.3), e <= 1);
  leavesFg(fg, 2);
  protoBoxFg(fg);
  return {
    lights: [
      { px: 240, py: 172, color: [0.82, 0.64, 0.42], radius: 230, strength: 1.4 },
      { px: 240, py: 58, color: [0.9, 0.7, 0.45], radius: 70, strength: 1.2 },
      { px: 75, py: 50, color: [0.62, 0.42, 0.34], radius: 95, strength: 1.0 },
    ],
    shafts: [{ pts: [[40, 22], [108, 22], [250, 270], [112, 270]] }, { pts: [[224, 52], [256, 52], [306, 178], [174, 178]] }],
    cat: { x: 437, y: 6 },
  };
}

/** The first office: a plain meeting room with blinds, a whiteboard of rule sketches and a strip light. */
function officeRoom(sc: Bitmap, em: Bitmap, fg: Bitmap, year: number, r: () => number): RoomOut {
  const e = furnitureEra(year);
  const wall: RGB = ([[214, 204, 176], [198, 194, 182], [206, 208, 204], [224, 224, 220]] as RGB[])[e];
  const carpet: RGB = ([[150, 98, 62], [96, 104, 122], [104, 110, 118], [92, 98, 106]] as RGB[])[e];
  sc.rect(0, 0, CW, 124, wall);
  if (e === 0) {
    // seventies wood panelling on the lower wall
    sc.rect(0, 84, CW, 36, [140, 92, 56]);
    for (let x = 0; x < CW; x += 10) sc.line(x, 84, x, 120, [118, 76, 46]);
  }
  if (e === 3) sc.rect(0, 0, 130, 124, [92, 140, 150]);
  // ceiling tiles
  sc.rect(0, 0, CW, 8, [230, 228, 220]);
  for (let x = 0; x < CW; x += 32) sc.line(x, 0, x, 7, [200, 198, 190]);
  sc.line(0, 8, CW, 8, [170, 168, 160]);
  sc.rect(0, 118, CW, 6, shade(wall, 0.62));
  // window with venetian blinds (the sky shows between the slats)
  sc.box(32, 14, 116, 84, [200, 200, 196]);
  sc.box(34, 16, 114, 82, [150, 152, 156]);
  drawSky(sc, false);
  drawSky(em, true);
  for (let y = 20; y <= 79; y += 3) {
    sc.line(38, y, 110, y, [226, 224, 214]);
    sc.line(38, y + 1, 110, y + 1, [190, 188, 180]);
    for (let x = 38; x <= 110; x++) {
      em.clear(x, y);
      em.clear(x, y + 1);
    }
  }
  sc.box(38, 79, 110, 81, [226, 224, 214]);
  sc.line(106, 20, 106, 92, [200, 198, 190]);
  sc.box(105, 92, 107, 96, [180, 178, 170]);
  // whiteboard of rule sketches
  sc.box(136, 16, 252, 78, [170, 172, 178]);
  sc.box(138, 18, 250, 74, [246, 246, 242]);
  sc.strokeBox(146, 26, 166, 36, [70, 110, 200]);
  sc.strokeBox(184, 24, 204, 34, [70, 110, 200]);
  sc.poly([[222, 26], [230, 32], [222, 38], [214, 32]], [246, 246, 242]);
  for (const [a, b2] of [[[166, 31], [184, 29]], [[204, 29], [214, 32]], [[222, 38], [222, 48]]] as [number, number][][]) sc.line(a[0], a[1], b2[0], b2[1], [60, 60, 70]);
  sc.line(214, 32, 222, 26, [200, 70, 70]);
  sc.line(222, 26, 230, 32, [200, 70, 70]);
  sc.line(230, 32, 222, 38, [200, 70, 70]);
  sc.line(222, 38, 214, 32, [200, 70, 70]);
  let py = 62;
  for (let x = 146; x < 210; x += 4) {
    const ny = 50 + Math.round(8 * Math.sin(x / 9) + r() * 3);
    sc.line(x, py, x + 4, ny, [60, 150, 90]);
    py = ny;
  }
  for (const [nx, ny, c] of [[226, 50, [250, 222, 110]], [236, 58, [244, 170, 180]], [214, 60, [160, 210, 236]]] as [number, number, RGB][]) sc.box(nx, ny, nx + 7, ny + 7, c);
  sc.box(138, 75, 250, 77, [150, 152, 158]);
  for (const [mx, c] of [[160, [200, 60, 60]], [168, [60, 100, 200]], [176, [40, 40, 46]]] as [number, RGB][]) sc.box(mx, 74, mx + 5, 75, c);
  eraPoster(sc, 266, 22, year, [40, 40, 46]);
  wallClock(sc, 334, 30, [60, 62, 70]);
  // water cooler
  sc.box(322, 88, 342, 120, [226, 226, 222]);
  sc.box(324, 64, 340, 88, [150, 200, 230]);
  sc.ellipse(324, 60, 340, 70, [150, 200, 230]);
  sc.line(326, 66, 326, 86, [210, 236, 250]);
  sc.box(328, 96, 336, 98, [70, 120, 200]);
  // a bookcase of binders and a few game boxes
  sc.box(352, 16, 470, 120, [128, 116, 104]);
  sc.box(355, 19, 467, 117, [70, 62, 58]);
  for (const base of [52, 86, 117]) {
    sc.box(352, base, 470, base + 2, [128, 116, 104]);
    let x = 357;
    while (x < 462) {
      if (base === 86 && x > 420) {
        for (let k = 0; k < 3; k++) sc.box(x, base - 6 * (k + 1), x + 26, base - 6 * k - 1, BOXC[(k * 3 + 1) % BOXC.length]);
        break;
      }
      const c = ([[70, 110, 190], [200, 70, 70], [70, 150, 100], [230, 200, 80], [90, 90, 100]] as RGB[])[Math.floor(r() * 5)];
      const bh = 22 + Math.floor(r() * 6);
      sc.box(x, base - bh, x + 5, base - 1, c);
      sc.box(x + 1, base - bh + 4, x + 4, base - bh + 8, [240, 240, 236]);
      x += 7;
    }
  }
  tiledFloor(sc, 7, 34, (k, i) => shade(carpet, (k + i) % 2 ? 0.95 : 1.03), shade(carpet, 0.85));
  for (let i = 0; i < 700; i++) sc.set(Math.floor(r() * CW), 124 + Math.floor(r() * (CH - 124)), shade(carpet, r() < 0.5 ? 0.88 : 1.1));
  tableShadow(sc);
  sc.ellipse(66, 197, 96, 203, [20, 10, 20], 120);
  bigPlant(sc);
  chairs(sc, e === 0 ? "chrome" : "office", e === 0 ? [176, 110, 60] : [64, 66, 76], e === 0 ? [184, 188, 196] : [120, 122, 130]);
  // the strip light over the table
  for (const wx of [206, 274]) sc.line(wx, 8, wx, 40, [90, 90, 96]);
  sc.box(198, 40, 282, 45, [206, 206, 210]);
  sc.line(198, 40, 282, 40, [236, 236, 240]);
  sc.box(200, 46, 280, 47, [244, 248, 250]);
  em.box(200, 46, 280, 48, [236, 244, 255]);
  leavesFg(fg, 3);
  protoBoxFg(fg);
  return {
    lights: [
      { px: 240, py: 172, color: [0.78, 0.78, 0.76], radius: 230, strength: 1.35 },
      { px: 240, py: 52, color: [0.86, 0.9, 0.95], radius: 80, strength: 1.0 },
      { px: 75, py: 50, color: [0.62, 0.42, 0.34], radius: 95, strength: 0.9 },
    ],
    shafts: [
      { pts: [[40, 30], [108, 30], [200, 262], [110, 262]] },
      { pts: [[40, 52], [108, 52], [180, 270], [96, 270]] },
      { pts: [[200, 48], [280, 48], [314, 180], [166, 180]] },
    ],
    cat: { x: 30, y: 230 },
  };
}

/** Headquarters: a proper test lab with an observation mirror, a camera recording the table, and a live fun curve on the wall. */
function hqRoom(sc: Bitmap, em: Bitmap, fg: Bitmap, year: number, r: () => number): RoomOut {
  sc.rect(0, 0, CW, 124, [214, 218, 222]);
  sc.rect(0, 0, CW, 6, [238, 240, 242]);
  sc.line(0, 6, CW, 6, [180, 184, 190]);
  for (let x = 20; x < CW; x += 60) {
    sc.box(x, 2, x + 8, 5, [60, 62, 70]);
    em.box(x + 2, 5, x + 6, 6, [255, 250, 236]);
  }
  // acoustic panels along the lower wall
  sc.rect(0, 88, CW, 30, [74, 128, 132]);
  for (let x = 0; x < CW; x += 24) sc.line(x, 88, x, 118, [62, 108, 112]);
  sc.line(0, 88, CW, 88, [120, 170, 172]);
  sc.rect(0, 118, CW, 6, [150, 156, 162]);
  // the observation mirror: dark glass, the people watching barely visible behind it, their monitor glowing
  sc.box(20, 12, 152, 86, [168, 174, 182]);
  for (let y = 14; y <= 84; y++) sc.line(22, y, 150, y, mix([30, 38, 52], [58, 70, 88], (y - 14) / 70));
  sc.box(22, 72, 150, 84, [24, 30, 42]);
  for (const [hx, hy] of [[62, 50], [112, 48]]) {
    sc.ellipse(hx - 6, hy, hx + 6, hy + 12, [22, 28, 38]);
    sc.ellipse(hx - 12, hy + 12, hx + 12, hy + 30, [22, 28, 38]);
  }
  sc.box(80, 58, 96, 68, [70, 108, 140]);
  em.box(80, 58, 96, 68, [40, 70, 100]);
  for (let k = 0; k < 4; k++) {
    sc.line(26 + k * 34, 84, 52 + k * 34, 14, [255, 255, 255], 34);
    sc.line(29 + k * 34, 84, 55 + k * 34, 14, [255, 255, 255], 20);
  }
  sc.ellipse(118, 30, 136, 38, [255, 220, 170], 40);
  sc.box(20, 86, 152, 88, [190, 196, 204]);
  // framed con poster and a certificate
  eraPoster(sc, 166, 22, year, [40, 40, 46]);
  sc.box(226, 26, 260, 56, [180, 150, 80]);
  sc.box(228, 28, 258, 54, [246, 240, 222]);
  for (let k = 0; k < 3; k++) sc.line(233, 33 + k * 4, 253, 33 + k * 4, [150, 140, 130]);
  sc.ellipse(237, 44, 247, 52, [214, 170, 70]);
  sc.line(240, 52, 238, 56, [200, 60, 60]);
  sc.line(244, 52, 246, 56, [200, 60, 60]);
  // the live fun curve on a wall screen
  sc.box(296, 20, 382, 70, [30, 32, 40]);
  sc.box(299, 23, 379, 66, [22, 36, 64]);
  em.box(299, 23, 379, 66, [12, 22, 44]);
  let ly = 50;
  for (let x = 303; x < 375; x += 4) {
    const ny = 34 + Math.round(10 * Math.sin(x / 11) + r() * 6);
    sc.line(x, ly, x + 4, ny, [250, 200, 80]);
    em.line(x, ly, x + 4, ny, [200, 150, 50]);
    ly = ny;
  }
  for (let k = 0; k < 6; k++) sc.box(304 + k * 12, 62 - k * 1 - Math.floor(r() * 5), 310 + k * 12, 64, [70, 190, 190]);
  sc.box(372, 26, 374, 28, [240, 60, 60]);
  em.box(372, 26, 374, 28, [255, 60, 60]);
  sc.box(336, 70, 342, 76, [60, 62, 70]);
  // white shelving of labelled prototypes
  sc.box(392, 12, 470, 120, [232, 232, 228]);
  sc.box(395, 15, 467, 117, [198, 202, 206]);
  for (const base of [44, 78, 112]) {
    sc.box(392, base, 470, base + 2, [232, 232, 228]);
    let x = 398;
    while (x < 460) {
      const c = BOXC[Math.floor(r() * BOXC.length)];
      const bw = 12 + Math.floor(r() * 6);
      const bh = 12 + Math.floor(r() * 10);
      if (x + bw > 464) break;
      sc.box(x, base - bh, x + bw, base - 1, c);
      sc.box(x + 2, base - bh + 3, x + bw - 2, base - bh + 5, [250, 250, 246]);
      x += bw + 3;
    }
  }
  sc.box(434, 4, 440, 11, [160, 120, 50]);
  sc.poly([[431, -2], [443, -2], [440, 6], [434, 6]], [236, 190, 80]);
  // the dome camera on the ceiling and a boom mic over the table
  sc.box(166, 0, 174, 4, [190, 194, 200]);
  sc.ellipse(163, 3, 177, 12, [40, 44, 56]);
  sc.set(167, 6, [140, 160, 190]);
  sc.set(173, 9, [255, 70, 60]);
  em.set(173, 9, [255, 70, 60]);
  sc.line(292, 0, 292, 62, [60, 62, 70]);
  sc.ellipse(287, 61, 297, 74, [70, 72, 80]);
  sc.line(289, 64, 289, 70, [110, 112, 120]);
  // polished floor
  tiledFloor(sc, 6, 44, (k, i) => ((k + i) % 2 ? [184, 188, 192] : [176, 180, 186]), [160, 164, 170]);
  for (let k = 0; k < 3; k++) sc.line(170 + k * 50, 126, 150 + k * 60, 268, [236, 240, 244], 50);
  tableShadow(sc);
  sc.ellipse(66, 197, 96, 203, [20, 10, 20], 110);
  // the camera on its tripod, recording the table
  sc.ellipse(398, 236, 452, 244, [20, 10, 20], 90);
  for (const [fx, fy] of [[402, 240], [448, 240], [428, 236]]) sc.line(424, 152, fx, fy, [50, 52, 60]);
  sc.line(425, 152, 403, 240, [90, 92, 100]);
  sc.box(418, 146, 430, 152, [60, 62, 70]);
  sc.box(404, 130, 436, 146, [44, 46, 54]);
  sc.line(404, 130, 436, 130, [90, 94, 104]);
  sc.box(396, 133, 404, 143, [30, 32, 38]);
  sc.ellipse(393, 134, 399, 142, [70, 100, 150]);
  sc.set(395, 136, [200, 220, 250]);
  sc.box(436, 130, 446, 139, [30, 32, 38]);
  sc.box(437, 131, 445, 138, [70, 140, 190]);
  em.box(437, 131, 445, 138, [50, 100, 150]);
  sc.box(430, 132, 432, 134, [250, 60, 60]);
  em.box(429, 131, 433, 135, [255, 60, 60]);
  // the cat's bed
  sc.ellipse(112, 222, 150, 240, [120, 80, 150]);
  sc.ellipse(116, 224, 146, 236, [170, 130, 200]);
  sleepingCat(sc, 120, 224);
  chairs(sc, "moulded", [228, 124, 72], [170, 174, 182]);
  sc.line(240, 6, 240, 42, [80, 82, 90]);
  sc.ellipse(220, 42, 260, 52, [40, 40, 46]);
  sc.ellipse(225, 44, 255, 50, [214, 218, 222]);
  sc.ellipse(222, 46, 258, 52, [250, 250, 244]);
  sc.ellipse(228, 46, 252, 50, [40, 40, 46]);
  em.ellipse(222, 47, 258, 53, [255, 250, 240]);
  em.ellipse(229, 47, 251, 50, [0, 0, 0]);
  leavesFg(fg, 2);
  fg.box(410, 248, 480, 270, [200, 204, 210]);
  fg.box(426, 238, 460, 248, [44, 46, 54]);
  fg.box(428, 240, 458, 246, [80, 130, 180]);
  return {
    lights: [
      { px: 240, py: 172, color: [0.84, 0.82, 0.78], radius: 240, strength: 1.35 },
      { px: 240, py: 52, color: [0.9, 0.92, 0.96], radius: 80, strength: 1.0 },
      { px: 340, py: 44, color: [0.35, 0.55, 0.85], radius: 50, strength: 0.8 },
      { px: 86, py: 50, color: [0.3, 0.38, 0.5], radius: 60, strength: 0.6 },
    ],
    shafts: [{ pts: [[222, 50], [258, 50], [312, 180], [168, 180]] }],
    cat: { x: 136, y: 226 },
  };
}

/**
 * The playtest room close-up. The room follows your building (opts.level): a kitchen table at home in the garage,
 * a plain meeting room in the first office, the games lab in the studio, a lab with an observation mirror at HQ.
 * Posters and props follow the year. A number as the second argument is the old (seed) form, kept for callers.
 */
export function playtestCloseup(year: number, opts: PlaytestRoomOpts | number = {}, wall?: RGB): CloseupScene {
  const o: PlaytestRoomOpts = typeof opts === "number" ? { seed: opts, wall } : { ...opts, wall: opts.wall ?? wall };
  const level: BuildingLevel = LEVELS.includes(o.level as BuildingLevel) ? (o.level as BuildingLevel) : "studio";
  const r = artRng((o.seed ?? 7) + (level === "studio" ? 0 : 101 * (1 + LEVELS.indexOf(level))));
  const sc = new Bitmap(CW, CH);
  const em = new Bitmap(CW, CH);
  const fg = new Bitmap(CW, CH);
  const out =
    level === "garage" ? kitchenRoom(sc, em, fg, year, r)
    : level === "office" ? officeRoom(sc, em, fg, year, r)
    : level === "hq" ? hqRoom(sc, em, fg, year, r)
    : studioRoom(sc, em, fg, year, r, o.wall ?? [70, 92, 82]);
  const seats: CloseupSeat[] = [
    { x: 193, y: 112, view: "front", hx: 206, hy: 112 },
    { x: 263, y: 110, view: "front", hx: 276, hy: 110 },
    { x: 185, y: 186, view: "back", hx: 198, hy: 186 },
    { x: 271, y: 181, view: "back", hx: 284, hy: 176 },
    { x: 140, y: 140, view: "front", hx: 153, hy: 140 },
    { x: 318, y: 140, view: "front", hx: 331, hy: 140 },
  ];
  const cells: [number, number][] = [];
  for (let rr = 0; rr < 5; rr++) for (let c = 0; c < 8; c++) cells.push([0.17 + (0.66 * (c + 0.5)) / 8, 0.14 + (0.66 * (rr + 0.5)) / 5]);
  return {
    kind: "playtest", img: sc, em, fg, seats, designer: { x: 68, y: 152, view: "front", hx: 81, hy: 152 }, table: { cells, tp: tableTp },
    lights: out.lights, shafts: out.shafts, cat: out.cat, room: level, year,
  };
}

// ------------------------------------------------------------------ the table: what this game is

export interface PlaytestTableOpts {
  /** mechanic id: sets what lies on the table (hands of cards, a hex map, a track, worker spaces, dice...) */
  mechanic?: string;
  /** theme id: the board and card backs take its palette */
  theme?: string;
  /** format id: dice games get dice, RPG books a rulebook and a screen, miniatures boxes minis */
  format?: string;
  year?: number;
  /** the game's name: seeds where the pieces lie, so no two games' tables are the same */
  name?: string;
  seed?: number;
  pieces?: number;
}

type Surface = "track" | "ring" | "hex" | "network" | "city" | "workers" | "slots" | "tiles" | "dungeon" | "terrain" | "mats" | "flick" | "grid" | "none";

interface Kit {
  surface: Surface;
  hands?: boolean;
  tableau?: number;
  market?: number;
  trick?: boolean;
  deck?: boolean;
  decks?: boolean;
  roles?: boolean;
  story?: boolean;
  dice?: number;
  poly?: number;
  cup?: boolean;
  pawns?: number;
  meeples?: number;
  minis?: number;
  monster?: boolean;
  cubes?: number;
  chits?: number;
  coins?: boolean;
  timer?: boolean;
  tower?: boolean;
  bag?: boolean;
  tokens?: number;
  sheets?: boolean;
  pads?: boolean;
  tablet?: boolean;
  glasses?: boolean;
  screen?: boolean;
  tome?: boolean;
  resources?: boolean;
  boards?: boolean;
  stickers?: boolean;
  tape?: boolean;
}

/** What a game of this mechanic and format has out on the table. */
function kitFor(mechanic: string | undefined, format: string | undefined, pieces: number): Kit {
  const board = format === "board";
  const k: Kit = (() => {
    switch (mechanic) {
      case "roll_move": return { surface: "track", pawns: 4, dice: 2, deck: true };
      case "trivia": return board ? { surface: "ring", pawns: 4, deck: true, dice: 1 } : { surface: "none", deck: true, tableau: 2, timer: true, pads: true };
      case "set_collection": return board ? { surface: "slots", hands: true } : { surface: "none", hands: true, tableau: 4, deck: true };
      case "trick_taking": return { surface: "none", hands: true, trick: true, pads: true };
      case "auction": return board ? { surface: "slots", coins: true, hands: true } : { surface: "none", market: 1, coins: true, hands: true, deck: true };
      case "dexterity": return board ? { surface: "flick" } : { surface: "none", tower: true, dice: format === "dice" ? 3 : 0 };
      case "push_luck": return board ? { surface: "track", dice: 5, cup: true, pawns: 4 } : format === "card" ? { surface: "none", deck: true, market: 4, tokens: 6 } : { surface: "none", dice: 6, cup: true, pads: true };
      case "hidden_movement": return { surface: "city", pawns: 4, screen: true, pads: true };
      case "campaign_rpg": return { surface: "dungeon", minis: 4, monster: true, poly: 4, screen: true, tome: true, sheets: true };
      case "wargame": return { surface: "hex", chits: 14, dice: 2 };
      case "area_control": return { surface: "hex", cubes: 16, hands: true };
      case "miniature_skirmish": return { surface: "terrain", minis: 10, dice: 4, tape: true };
      case "collectible_cards": return { surface: "mats", hands: true, poly: 2 };
      case "worker_placement": return { surface: "workers", meeples: 8, resources: true, boards: true };
      case "tile_laying": return { surface: "tiles", meeples: 4 };
      case "drafting": return board ? { surface: "slots", hands: true } : { surface: "none", hands: true, tableau: 4 };
      case "engine_building": return board ? { surface: "slots", boards: true, cubes: 6 } : { surface: "none", tableau: 5, market: 4, deck: true, tokens: 8 };
      case "deck_building": return { surface: "none", market: 5, deck: true, decks: true, hands: true };
      case "coop": return board ? { surface: "network", pawns: 4, cubes: 12, hands: true } : { surface: "none", market: 3, deck: true, hands: true, tokens: 6 };
      case "social_deduction": return { surface: "none", roles: true, tokens: 10 };
      case "roll_write": return { surface: "none", sheets: true, dice: 6 };
      case "app_hybrid": return { surface: "dungeon", tablet: true, minis: 4 };
      case "mixed_reality": return { surface: "terrain", tablet: true, glasses: true, minis: 4 };
      case "ai_gm": return format === "rpg_book" ? { surface: "dungeon", tablet: true, minis: 4, poly: 3, sheets: true } : { surface: "network", tablet: true, pawns: 4 };
      case "legacy": return { surface: "hex", stickers: true, cubes: 8, hands: true };
      case "dice_drafting": return { surface: board ? "workers" : "none", dice: 9, boards: true };
      case "real_time": return { surface: board ? "tiles" : "none", timer: true, market: format === "card" ? 5 : 0, hands: format === "card", dice: format === "dice" ? 6 : 0 };
      case "storytelling": return { surface: "none", story: true, hands: true, tokens: 6 };
      case "bag_building": return { surface: "slots", bag: true, tokens: 10, boards: true };
      case "negotiation": return board ? { surface: "hex", coins: true, hands: true, cubes: 8 } : { surface: "none", coins: true, hands: true, tableau: 2 };
      default: return format === "card" || format === "ccg" ? { surface: "none", hands: true, deck: true, market: 3 } : { surface: "grid", pawns: pieces };
    }
  })();
  if (format === "dice") k.dice = Math.max(k.dice ?? 0, 5);
  if (format === "rpg_book") {
    k.tome = true;
    k.poly = Math.max(k.poly ?? 0, 3);
  }
  if (format === "minis_box") k.minis = Math.max(k.minis ?? 0, 6);
  if (format === "ccg") k.surface = "mats";
  return k;
}

const PC: RGB[] = [[220, 60, 60], [60, 110, 220], [240, 200, 60], [60, 170, 90], [150, 80, 190], [236, 130, 50]];

/** Tiny sprites from text rows, outlined so they read on any table. */
function spr(rows: string[], map: Record<string, RGB>, outline = true): Bitmap {
  const bm = new Bitmap(rows[0].length, rows.length);
  rows.forEach((row, y) => [...row].forEach((ch, x) => {
    const c = map[ch];
    if (c) bm.set(x, y, c);
  }));
  return outline ? bm.outlined(INK) : bm;
}

const pawnSpr = (c: RGB) => spr([".l.", "lcd", ".c.", "lcd", "ccd"], { l: shade(c, 1.4), c, d: shade(c, 0.72) });
const meepleSpr = (c: RGB) => spr(["..l..", "lcccd", ".ccd.", ".c.d."], { l: shade(c, 1.3), c, d: shade(c, 0.75) });
const miniSpr = (c: RGB, skin: RGB = [230, 186, 150]) => spr(["..s..", ".ccc.", "lcccd", ".ccd.", ".c.d.", "bbbbb"], { s: skin, l: shade(c, 1.3), c, d: shade(c, 0.7), b: [60, 60, 70] });
const monsterSpr = (c: RGB) => spr(["c.....c", "ccccccc", ".cecec.", "lcccccd", ".ccccd.", ".cc.cd.", "bbbbbbb"], { c, l: shade(c, 1.3), d: shade(c, 0.7), e: [250, 230, 90], b: [60, 60, 70] });
const cubeSpr = (c: RGB) => spr(["ll", "cd"], { l: shade(c, 1.35), c, d: shade(c, 0.75) });
const chitSpr = (c: RGB) => spr(["lll", "cmc", "ccd"], { l: shade(c, 1.3), c, d: shade(c, 0.75), m: INK });
const treeSpr = (c: RGB) => spr([".ll.", "lccc", "cccd", ".cd.", ".t."], { l: shade(c, 1.35), c, d: shade(c, 0.72), t: [110, 70, 44] });
const ruinSpr = () => spr(["l..l", "l.ll", "cccd"], { l: [190, 186, 180], c: [150, 146, 140], d: [110, 106, 104] });
const timerSpr = () => spr(["wwwwwww", ".gsssg.", "..gsg..", "...s...", "..gsg..", ".gs.sg.", ".sssss.", "wwwwwww"], { w: [130, 80, 50], g: [196, 226, 236], s: [236, 196, 110] });
const bagSpr = (c: RGB) => spr(["..t.t..", "...k...", ".ccccc.", "lcccccd", "ccccccd", ".cccdd."], { t: [230, 210, 160], k: shade(c, 0.6), c, l: shade(c, 1.25), d: shade(c, 0.75) });
const cupSpr = () => spr(["llll", "lccd", "lccd", "lccd", ".cd."], { l: [170, 110, 70], c: [130, 78, 46], d: [96, 56, 34] });

function dieSpr(c: RGB, pip: RGB, face: number): Bitmap {
  const pips: string[][] = [["....", "....", ".p..", "...."], ["....", "p...", "....", "..p."], ["....", "p...", ".p..", "..p."], ["....", "p.p.", "....", "p.p."]];
  const rows = ["llll", ...pips[face % 4].slice(1)].map((row, y) => (y === 0 ? row : row.replace(/\./g, "c")));
  return spr(rows, { l: shade(c, 1.15), c, p: pip });
}

const polySpr = (c: RGB) => spr([".ll.", "lccd", "cmcd", ".dd."], { l: shade(c, 1.35), c, d: shade(c, 0.7), m: shade(c, 1.6) });

function towerSpr(r: () => number): Bitmap {
  const W = 9;
  const H = 18;
  const bm = new Bitmap(W, H);
  const wood: RGB = [214, 172, 112];
  for (let layer = 0; layer < 9; layer++) {
    const y = H - 2 - layer * 2;
    const off = layer > 5 ? (r() < 0.5 ? -1 : 1) : 0;
    if (layer % 2) {
      bm.line(0 + off, y, W - 1 + off, y, shade(wood, 1.2));
      bm.line(0 + off, y + 1, W - 1 + off, y + 1, shade(wood, 0.85));
    } else {
      for (let k = 0; k < 3; k++) {
        if (layer > 2 && r() < 0.25) continue;
        bm.box(k * 3 + off, y, k * 3 + 1 + off, y + 1, k % 2 ? shade(wood, 0.95) : wood);
      }
    }
  }
  return bm.outlined(INK);
}

interface Pal {
  dark: RGB;
  mid: RGB;
  light: RGB;
}

function themePal(theme?: string): Pal {
  const hx = (theme ? C.theme[theme]?.palette : undefined) ?? ["#3A6AC8", "#E85848", "#F4E8D0"];
  const cs = hx.map(hex).sort((a, b) => lum(a) - lum(b));
  return { dark: cs[0], mid: cs[1], light: cs[2] };
}

/** The table itself, dressed for the room: wood in the lab, a gingham cloth at home, laminate at the office, white at HQ. */
function tableBody(b: Bitmap, room: BuildingLevel, year: number): void {
  const e = furnitureEra(year);
  if (room === "garage") {
    const cloth: RGB | null = e === 0 ? [206, 64, 60] : e === 1 ? [214, 126, 52] : e === 2 ? [70, 110, 190] : null;
    if (cloth) {
      const top: [number, number][] = [[168, TB - 1], [312, TB - 1], [330, TF + 1], [150, TF + 1]];
      b.poly(top, CREAM);
      const T = (u: number, v: number): [number, number] => [168 + 144 * u + (150 + 180 * u - (168 + 144 * u)) * v, TB - 1 + (TF + 2 - TB) * v];
      const nu = 18;
      const nv = 6;
      for (let i = 0; i < nv; i++) for (let j = 0; j < nu; j++) {
        const on = (i % 2) + (j % 2);
        if (!on) continue;
        const c = on === 2 ? cloth : mix(cloth, CREAM, 0.5);
        b.poly([T(j / nu, i / nv), T((j + 1) / nu, i / nv), T((j + 1) / nu, (i + 1) / nv), T(j / nu, (i + 1) / nv)], c);
      }
      // the cloth hangs over the front edge
      for (let x = 150; x <= 330; x++) {
        const j = Math.floor((x - 150) / 10);
        const drop = 9 + ((x - 150) % 10 < 5 ? 0 : 1);
        for (let y = TF + 2; y <= TF + drop; y++) {
          const i = Math.floor((y - TF - 2) / 4);
          const on = (i % 2) + (j % 2);
          b.set(x, y, shade(on === 2 ? cloth : on ? mix(cloth, CREAM, 0.5) : CREAM, 0.82));
        }
      }
      for (const lx of [158, 318]) b.box(lx, TF + 11, lx + 4, TF + 26, e <= 1 ? [184, 188, 196] : [150, 104, 64]);
      return;
    }
    // a light oak kitchen table with a runner
    b.poly([[172, TB], [308, TB], [326, TF], [154, TF]], [196, 156, 106]);
    for (let i = 1; i < 10; i++) b.line(172 + 13.6 * i, TB, 154 + 17.2 * i, TF, [186, 146, 98]);
    b.box(154, TF + 1, 326, TF + 6, [160, 120, 80]);
    for (const lx of [158, 318]) b.box(lx, TF + 7, lx + 4, TF + 26, [150, 112, 74]);
    return;
  }
  if (room === "office") {
    b.poly([[172, TB], [308, TB], [326, TF], [154, TF]], [150, 152, 150]);
    for (let i = 1; i < 6; i++) b.line(172 + 22 * i, TB, 160 + 26 * i, TF, [158, 160, 158]);
    b.line(154, TF, 326, TF, [190, 192, 190]);
    b.box(154, TF + 1, 326, TF + 4, [110, 112, 118]);
    for (const lx of [160, 316]) b.box(lx, TF + 5, lx + 3, TF + 26, [150, 152, 158]);
    b.box(170, TF + 5, 310, TF + 14, [130, 132, 138]);
    return;
  }
  if (room === "hq") {
    b.poly([[172, TB], [308, TB], [326, TF], [154, TF]], [204, 208, 214]);
    b.line(172, TB, 308, TB, [184, 188, 194]);
    b.line(154, TF, 326, TF, [250, 250, 248]);
    b.box(154, TF + 1, 326, TF + 3, [196, 198, 202]);
    b.line(154, TF + 4, 326, TF + 4, [60, 62, 70]);
    for (const lx of [176, 300]) {
      b.box(lx, TF + 5, lx + 5, TF + 24, [70, 72, 80]);
      b.box(lx - 6, TF + 24, lx + 11, TF + 26, [70, 72, 80]);
    }
    return;
  }
  b.poly([[172, TB], [308, TB], [326, TF], [154, TF]], [118, 70, 44]);
  for (let i = 1; i < 14; i++) {
    const t = i / 14;
    b.line(172 + 136 * t, TB, 154 + 172 * t, TF, [110, 64, 40]);
  }
  b.line(172, TB, 308, TB, [92, 54, 34]);
  b.line(154, TF, 326, TF, [176, 118, 76]);
  b.box(154, TF + 1, 326, TF + 8, [80, 46, 30]);
  b.line(154, TF + 8, 326, TF + 8, [56, 32, 22]);
  for (const lx of [158, 318]) b.box(lx, TF + 9, lx + 4, TF + 26, [70, 42, 28]);
}

const BU0 = 0.2;
const BU1 = 0.8;
const BV0 = 0.15;
const BV1 = 0.72;

/** Where each seat's things go on the table (u across, v front-to-back); the near ones sit just inside the near testers' backs. */
const SPOTS: { u: number; v: number; side: "far" | "near" | "left" | "right" }[] = [
  { u: 0.25, v: 0.03, side: "far" },
  { u: 0.76, v: 0.03, side: "far" },
  { u: 0.4, v: 0.9, side: "near" },
  { u: 0.6, v: 0.9, side: "near" },
  { u: 0.06, v: 0.48, side: "left" },
  { u: 0.94, v: 0.48, side: "right" },
];

/**
 * The table and what is on it, drawn by the renderer between the far and near rows of testers.
 * It shows what this game is: fanned hands and a draw pile for card games, a board whose art follows the mechanic
 * (a track, a hex map, worker spaces, laid tiles, a dungeon...), dice, minis, a sand timer, a rulebook, the box lid,
 * all in the theme's colours. Pieces are placed with a seed from the game's name.
 * A number as the second argument is the old (seed) form: a plain board with pawns.
 */
export function playtestTable(scene: CloseupScene, opts: PlaytestTableOpts | number = {}, pieces = 5): Bitmap {
  const o: PlaytestTableOpts = typeof opts === "number" ? { seed: opts, pieces } : opts;
  const b = new Bitmap(CW, CH);
  const tp = scene.table?.tp ?? tableTp;
  const room = scene.room ?? "studio";
  const year = o.year ?? scene.year ?? 1984;
  tableBody(b, room, year);
  const r = artRng(hashText(`${o.name ?? ""}|${o.mechanic ?? ""}|${o.theme ?? ""}|${o.format ?? ""}`) + (o.seed ?? 3) * 7919);
  const pal = themePal(o.theme);
  const kit = kitFor(o.mechanic, o.format, o.pieces ?? pieces);
  const e = furnitureEra(year);
  const stand: { x: number; y: number; s: Bitmap }[] = [];
  const P = (u: number, v: number): [number, number] => {
    const [x, y] = tp(u, v);
    return [Math.round(x), Math.round(y)];
  };
  const quad = (u0: number, v0: number, u1: number, v1: number, c: RGB, a = 255) => b.poly([tp(u0, v0), tp(u1, v0), tp(u1, v1), tp(u0, v1)], c, a);
  const bu = (s: number) => BU0 + (BU1 - BU0) * s;
  const bv = (t: number) => BV0 + (BV1 - BV0) * t;
  const bq = (s0: number, t0: number, s1: number, t1: number, c: RGB) => quad(bu(s0), bv(t0), bu(s1), bv(t1), c);
  const put = (s: Bitmap, u: number, v: number) => {
    const [x, y] = P(u, v);
    stand.push({ x: x - Math.floor(s.w / 2), y: y - s.h + 1, s });
  };
  const putB = (s: Bitmap, sx: number, ty: number) => put(s, bu(sx), bv(ty));
  const back = pal.dark;
  // cards: 7×6 with an ink edge so they read on any table; face down shows the theme's back, face up a coloured picture
  const card = (x: number, y: number, up: boolean, art?: RGB) => {
    b.box(x, y, x + 6, y + 5, up ? CREAM : back, INK);
    if (up) {
      b.box(x + 2, y + 1, x + 4, y + 2, art ?? pal.mid);
      b.line(x + 2, y + 4, x + 4, y + 4, [150, 140, 140]);
    } else {
      b.strokeBox(x + 1, y + 1, x + 5, y + 4, shade(back, 1.6));
      b.set(x + 3, y + 2, pal.mid);
      b.set(x + 3, y + 3, pal.mid);
    }
  };
  const bigCard = (x: number, y: number, up: boolean, art?: RGB) => {
    b.box(x, y, x + 8, y + 6, up ? CREAM : back, INK);
    if (up) {
      const a = art ?? pal.mid;
      b.box(x + 2, y + 1, x + 6, y + 3, a);
      b.set(x + 3 + Math.floor(r() * 3), y + 2, shade(a, 1.5));
      b.line(x + 2, y + 5, x + 5, y + 5, [150, 140, 140]);
    } else {
      b.strokeBox(x + 1, y + 1, x + 7, y + 5, shade(back, 1.6));
      b.box(x + 3, y + 2, x + 5, y + 4, pal.mid);
    }
  };
  const deck = (x: number, y: number, h: number, up = false) => {
    for (let k = 0; k < h; k++) b.line(x, y + 6 - k, x + 6, y + 6 - k, k % 2 ? [214, 206, 190] : INK);
    card(x, y - h, up);
  };
  const fan = (u: number, v: number, side: string, up: boolean) => {
    const [x, y] = P(u, v);
    const arc = [1, 0, -1, 0, 1];
    for (let i = 0; i < 5; i++) {
      if (side === "left" || side === "right") card(x - 3 + (side === "left" ? i : -i), y - 9 + i * 3, up, PC[(i * 2) % 6]);
      else card(x - 9 + i * 3, y - 3 + arc[i], up, PC[(i * 3) % 6]);
    }
  };
  const towards = (sp: (typeof SPOTS)[number], d: number): [number, number] => {
    if (sp.side === "far") return [sp.u, sp.v + d];
    if (sp.side === "near") return [sp.u, sp.v - d];
    if (sp.side === "left") return [sp.u + d * 0.55, sp.v];
    return [sp.u - d * 0.55, sp.v];
  };
  const hasBoard = kit.surface !== "none" && kit.surface !== "mats" && kit.surface !== "tiles";
  const nodes: [number, number][] = []; // board-local spots pieces can stand on

  // ---- the box lid (left end) and the rulebook (right end), in every game
  quad(0.015, 0.06, 0.13, 0.3, shade(pal.dark, 0.8));
  quad(0.025, 0.08, 0.12, 0.28, pal.dark);
  quad(0.03, 0.15, 0.115, 0.21, pal.light);
  quad(0.05, 0.1, 0.09, 0.14, pal.mid);
  if (e === 0) {
    quad(0.87, 0.07, 0.97, 0.27, [236, 230, 210]);
    for (let k = 0; k < 4; k++) quad(0.885, 0.1 + k * 0.04, 0.95, 0.115 + k * 0.04, [160, 150, 150]);
  } else {
    const th = e === 1 ? 1 : 2;
    const [x0, y0] = P(0.87, 0.07);
    const [x1, y1] = P(0.97, 0.27);
    b.box(x0, y0 + th, x1, y1 + th, [236, 230, 214]);
    b.box(x0, y0, x1, y1, pal.mid);
    b.box(x0 + 2, y0 + 2, x1 - 2, y0 + 3, pal.light);
    b.box(x0 + 3, y0 + 5, x1 - 3, y1 - 2, shade(pal.mid, 0.8));
  }

  // ---- the surface
  if (hasBoard) {
    quad(BU0 - 0.015, BV0 - 0.04, BU1 + 0.015, BV1 + 0.05, [48, 40, 56]);
    const [fx0, fy] = P(BU0 - 0.015, BV1 + 0.05);
    const [fx1] = P(BU1 + 0.015, BV1 + 0.05);
    b.line(fx0, fy + 1, fx1, fy + 1, [30, 24, 34]);
  }
  const base = lum(pal.dark) < 45 ? mix(pal.dark, pal.mid, 0.3) : pal.dark;
  switch (kit.surface) {
    case "track": {
      bq(0, 0, 1, 1, base);
      for (let i = 0; i < 26; i++) {
        const [x, y] = P(bu(r()), bv(r()));
        b.set(x, y, shade(base, 1.35));
      }
      const path: [number, number][] = [];
      const rows = [0.18, 0.5, 0.82];
      rows.forEach((t, ri) => {
        for (let k = 0; k < 9; k++) path.push([ri % 2 ? 0.94 - k * 0.11 : 0.06 + k * 0.11, t]);
        if (ri < 2) path.push([ri % 2 ? 0.06 : 0.94, t + 0.16]);
      });
      const cols = [pal.mid, pal.light, pal.mid, shade(pal.light, 0.86)];
      path.forEach(([s, t], i) => {
        const c = i === 0 ? [80, 180, 90] as RGB : i === path.length - 1 ? [240, 196, 80] as RGB : i % 5 === 3 ? mix(PC[0], pal.light, 0.2) : cols[i % 4];
        bq(s - 0.04, t - 0.08, s + 0.04, t + 0.08, shade(c, 0.7));
        bq(s - 0.04, t - 0.08, s + 0.04, t + 0.05, c);
        nodes.push([s, t + 0.06]);
      });
      break;
    }
    case "ring": {
      bq(0, 0, 1, 1, base);
      const ring: [number, number][] = [];
      for (let k = 0; k < 10; k++) ring.push([0.05 + k * 0.1, 0.1]);
      for (let k = 1; k < 4; k++) ring.push([0.95, 0.1 + k * 0.2]);
      for (let k = 0; k < 10; k++) ring.push([0.95 - k * 0.1, 0.9]);
      for (let k = 1; k < 4; k++) ring.push([0.05, 0.9 - k * 0.2]);
      ring.forEach(([s, t], i) => {
        bq(s - 0.042, t - 0.08, s + 0.042, t + 0.08, PC[i % 6]);
        nodes.push([s, t + 0.05]);
      });
      bq(0.3, 0.3, 0.7, 0.7, pal.light);
      bq(0.32, 0.34, 0.68, 0.66, pal.mid);
      break;
    }
    case "hex":
    case "grid": {
      if (kit.surface === "grid") {
        const pal5: RGB[] = [pal.mid, pal.light, shade(pal.mid, 0.85), shade(pal.light, 0.88), mix(pal.mid, pal.dark, 0.4)];
        for (let rr = 0; rr < 5; rr++) for (let c = 0; c < 8; c++) {
          const col = pal5[Math.floor(r() * 2.4) % 5];
          bq(c / 8, rr / 5, (c + 1) / 8, (rr + 1) / 5, (rr + c) % 2 ? shade(col, 0.92) : col);
          nodes.push([(c + 0.5) / 8, (rr + 0.7) / 5]);
        }
        break;
      }
      const sea = mix(base, [60, 110, 170], 0.3);
      bq(0, 0, 1, 1, sea);
      const [ax] = P(bu(0), bv(0.5));
      const [bx] = P(bu(1), bv(0.5));
      const W = bx - ax;
      const H = (BV1 - BV0) * (TF - TB);
      const rx = 4.2;
      const ry = 3.2;
      const cols = Math.floor(W / (rx * 1.73)) - 1;
      const rowsN = Math.floor(H / (ry * 1.5)) - 1;
      const regions: { s: number; t: number; c: RGB }[] = [];
      const rc: RGB[] = [pal.mid, shade(pal.mid, 0.82), pal.light, shade(pal.light, 0.84), mix(pal.mid, pal.light, 0.5)];
      for (let k = 0; k < 6; k++) regions.push({ s: r(), t: r(), c: rc[k % rc.length] });
      for (let row = 0; row < rowsN; row++) for (let col = 0; col < cols; col++) {
        const cxp = (col + 0.9 + (row % 2) * 0.5) * rx * 1.73;
        const cyp = (row + 0.9) * ry * 1.5;
        const s = cxp / W;
        const t = cyp / H;
        let best = regions[0];
        let bd = Infinity;
        for (const g of regions) {
          const d = (g.s - s) ** 2 * 3 + (g.t - t) ** 2;
          if (d < bd) {
            bd = d;
            best = g;
          }
        }
        const water = (s < 0.06 || s > 0.94 || t < 0.08 || t > 0.92) && r() < 0.6;
        const fill = water ? shade(sea, 1.15) : best.c;
        const pts: [number, number][] = [];
        for (let a = 0; a < 6; a++) {
          const ang = Math.PI / 6 + (a * Math.PI) / 3;
          pts.push(tp(bu((cxp + Math.cos(ang) * rx) / W), bv((cyp + Math.sin(ang) * ry) / H)));
        }
        b.poly(pts, fill);
        for (let a = 0; a < 6; a++) {
          const [p, q] = [pts[a], pts[(a + 1) % 6]];
          if (a >= 1 && a <= 3) b.line(Math.round(p[0]), Math.round(p[1]), Math.round(q[0]), Math.round(q[1]), mix(fill, INK, 0.35));
        }
        if (!water) nodes.push([s, t + 0.08]);
      }
      if (kit.stickers) for (let k = 0; k < 5; k++) {
        const [s, t] = nodes[Math.floor(r() * nodes.length)];
        const [x, y] = P(bu(s), bv(t - 0.1));
        b.box(x - 1, y - 1, x + 1, y, k % 2 ? [250, 250, 244] : [250, 214, 80]);
      }
      break;
    }
    case "network":
    case "city": {
      const city = kit.surface === "city";
      bq(0, 0, 1, 1, city ? mix(base, [60, 60, 70], 0.3) : mix(base, [50, 100, 170], 0.45));
      if (city) {
        for (let i = 0; i < 6; i++) for (let j = 0; j < 4; j++) {
          const c = (i + j) % 3 ? shade(pal.mid, 0.75 + r() * 0.2) : shade(pal.light, 0.7 + r() * 0.15);
          bq(i / 6 + 0.02, j / 4 + 0.05, (i + 1) / 6 - 0.02, (j + 1) / 4 - 0.05, c);
        }
      } else {
        for (let k = 0; k < 4; k++) {
          const s0 = 0.05 + k * 0.24;
          const t0 = 0.1 + r() * 0.2;
          bq(s0, t0, s0 + 0.2, t0 + 0.55 + r() * 0.2, k % 2 ? pal.mid : pal.light);
        }
      }
      const grid: [number, number][] = [];
      for (let i = 0; i < 6; i++) for (let j = 0; j < 3; j++) grid.push([0.08 + i * 0.168 + (r() - 0.5) * 0.05, 0.18 + j * 0.32 + (r() - 0.5) * 0.08]);
      const lineC: RGB[] = city ? [[250, 214, 70], [80, 190, 110], [230, 70, 70]] : [[244, 240, 228], [244, 240, 228], [250, 210, 90]];
      for (let i = 0; i < grid.length; i++) for (let j = i + 1; j < grid.length; j++) {
        const [s0, t0] = grid[i];
        const [s1, t1] = grid[j];
        const near = Math.abs(Math.round((s1 - s0) / 0.168)) + Math.abs(Math.round((t1 - t0) / 0.32));
        if (near !== 1 || r() < 0.15) continue;
        const [x0, y0] = P(bu(s0), bv(t0));
        const [x1, y1] = P(bu(s1), bv(t1));
        b.line(x0, y0, x1, y1, lineC[(i + j) % 3]);
      }
      for (const [s, t] of grid) {
        const [x, y] = P(bu(s), bv(t));
        b.box(x - 1, y - 1, x + 1, y, INK);
        b.set(x, y - 1, [250, 250, 244]);
        b.set(x, y, [250, 250, 244]);
        nodes.push([s, t + 0.04]);
      }
      break;
    }
    case "workers": {
      bq(0, 0, 1, 1, pal.light);
      bq(0, 0, 1, 0.12, pal.mid);
      for (let k = 0; k < 20; k++) bq(k / 20 + 0.005, 0.93, (k + 1) / 20 - 0.005, 1, k % 2 ? shade(pal.light, 0.8) : pal.mid);
      const res: RGB[] = [[160, 104, 60], [150, 150, 156], [240, 200, 70], [214, 70, 60]];
      for (let row = 0; row < 3; row++) for (let col = 0; col < 4; col++) {
        const s0 = 0.04 + col * 0.24;
        const t0 = 0.18 + row * 0.25;
        bq(s0, t0, s0 + 0.2, t0 + 0.19, shade(pal.mid, 0.7));
        bq(s0 + 0.01, t0 + 0.03, s0 + 0.19, t0 + 0.16, mix(pal.light, pal.mid, 0.4));
        const [x, y] = P(bu(s0 + 0.15), bv(t0 + 0.1));
        b.box(x - 1, y - 1, x, y, res[(row + col) % 4]);
        nodes.push([s0 + 0.07, t0 + 0.15]);
      }
      break;
    }
    case "slots": {
      bq(0, 0, 1, 1, mix(pal.mid, pal.dark, 0.25));
      for (let k = 0; k < 20; k++) bq(k / 20 + 0.005, 0.02, (k + 1) / 20 - 0.005, 0.1, k % 5 ? shade(pal.light, 0.85) : pal.light);
      for (let k = 0; k < 5; k++) {
        const s0 = 0.06 + k * 0.185;
        bq(s0, 0.24, s0 + 0.15, 0.78, shade(pal.dark, 0.8));
        bq(s0 + 0.01, 0.27, s0 + 0.14, 0.72, CREAM);
        bq(s0 + 0.025, 0.31, s0 + 0.125, 0.55, PC[(k * 2 + Math.floor(r() * 6)) % 6]);
        nodes.push([s0 + 0.07, 0.9]);
      }
      break;
    }
    case "flick": {
      const pts: [number, number][] = [];
      const pts2: [number, number][] = [];
      const pts3: [number, number][] = [];
      for (let a = 0; a < 32; a++) {
        const ang = (a / 32) * Math.PI * 2;
        pts.push(tp(0.5 + Math.cos(ang) * 0.3, 0.45 + Math.sin(ang) * 0.42));
        pts2.push(tp(0.5 + Math.cos(ang) * 0.24, 0.45 + Math.sin(ang) * 0.33));
        pts3.push(tp(0.5 + Math.cos(ang) * 0.06, 0.45 + Math.sin(ang) * 0.09));
      }
      b.poly(pts, [86, 54, 36]);
      b.poly(pts2, [214, 172, 112]);
      for (let a = 0; a < 8; a++) {
        const ang = (a / 8) * Math.PI * 2;
        put(spr(["l", "c"], { l: pal.light, c: pal.mid }), 0.5 + Math.cos(ang) * 0.15, 0.45 + Math.sin(ang) * 0.22);
      }
      b.poly(pts3, INK);
      for (let k = 0; k < 10; k++) {
        const ang = r() * Math.PI * 2;
        const d = 0.08 + r() * 0.14;
        const [x, y] = P(0.5 + Math.cos(ang) * d, 0.45 + Math.sin(ang) * d * 1.4);
        const c = k % 2 ? pal.dark : PC[k % 6];
        b.box(x - 1, y, x + 1, y + 1, c);
        b.line(x - 1, y, x + 1, y, shade(c, 1.4));
      }
      break;
    }
    case "dungeon": {
      bq(0, 0, 1, 1, [40, 36, 50]);
      const stone = mix([152, 146, 138], pal.mid, 0.18);
      const roomsD: [number, number, number, number][] = [[0.04, 0.08, 0.3, 0.55], [0.38, 0.06, 0.62, 0.46], [0.7, 0.14, 0.96, 0.62], [0.28, 0.58, 0.66, 0.94]];
      const corridors: [number, number, number, number][] = [[0.3, 0.26, 0.38, 0.38], [0.62, 0.24, 0.7, 0.36], [0.44, 0.46, 0.52, 0.58], [0.16, 0.55, 0.22, 0.8], [0.16, 0.74, 0.28, 0.84]];
      for (const [s0, t0, s1, t1] of [...roomsD, ...corridors]) bq(s0, t0, s1, t1, stone);
      for (const [s0, t0, s1, t1] of [...roomsD, ...corridors]) {
        for (let s = s0 + 0.05; s < s1 - 0.01; s += 0.05) {
          const [x0, y0] = P(bu(s), bv(t0));
          const [x1, y1] = P(bu(s), bv(t1));
          b.line(x0, y0, x1, y1, shade(stone, 0.88));
        }
        for (let t = t0 + 0.14; t < t1 - 0.02; t += 0.14) {
          const [x0, y0] = P(bu(s0), bv(t));
          const [x1, y1] = P(bu(s1), bv(t));
          b.line(x0, y0, x1, y1, shade(stone, 0.88));
        }
      }
      for (const [s0, t0, s1, t1] of roomsD) {
        const [x0, y0] = P(bu(s0), bv(t0));
        const [x1] = P(bu(s1), bv(t0));
        b.line(x0, y0, x1, y0, [90, 64, 50]);
        b.line(x0, y0 + 1, x1, y0 + 1, [120, 90, 70]);
        nodes.push([(s0 + s1) / 2 + (r() - 0.5) * 0.1, t0 + (t1 - t0) * (0.4 + r() * 0.4)]);
      }
      const [cx, cy] = P(bu(0.83), bv(0.4));
      b.box(cx - 2, cy - 1, cx + 2, cy + 1, [214, 170, 60]);
      b.line(cx - 2, cy - 1, cx + 2, cy - 1, [250, 220, 120]);
      break;
    }
    case "terrain": {
      const mat = mix(mix(pal.dark, pal.mid, 0.35), [90, 130, 70], 0.25);
      bq(0, 0, 1, 1, mat);
      for (let i = 0; i < 60; i++) {
        const [x, y] = P(bu(r()), bv(r()));
        b.set(x, y, shade(mat, r() < 0.5 ? 0.85 : 1.18));
      }
      const hill: [number, number][] = [];
      for (let a = 0; a < 16; a++) {
        const ang = (a / 16) * Math.PI * 2;
        hill.push(tp(bu(0.3 + Math.cos(ang) * 0.12), bv(0.5 + Math.sin(ang) * 0.22)));
      }
      b.poly(hill, shade(mat, 1.15));
      for (let k = 0; k < 4; k++) putB(treeSpr(mix([60, 130, 70], pal.mid, 0.25)), 0.55 + r() * 0.4, 0.35 + r() * 0.35);
      putB(ruinSpr(), 0.12 + r() * 0.1, 0.45);
      putB(ruinSpr(), 0.7, 0.55 + r() * 0.1);
      for (let k = 0; k < 8; k++) nodes.push([0.1 + r() * 0.8, k % 2 ? 0.12 + r() * 0.12 : 0.82 + r() * 0.12]);
      break;
    }
    case "mats": {
      for (const [v0, v1] of [[0.07, 0.4], [0.58, 0.9]]) {
        quad(0.17, v0, 0.83, v1, mix(pal.dark, INK, 0.25));
        quad(0.18, v0 + 0.03, 0.82, v1 - 0.03, mix(pal.dark, pal.mid, 0.2));
        quad(0.2, v0 + 0.06, 0.32, v1 - 0.06, shade(pal.mid, 0.6));
        const [x0, y0] = P(0.36, v0 + 0.08);
        for (let k = 0; k < 4; k++) bigCard(x0 + k * 10, y0, true, PC[(k + Math.floor(r() * 6)) % 6]);
        const [dx, dy] = P(0.76, v0 + 0.1);
        deck(dx, dy, 3);
      }
      break;
    }
    case "tiles": {
      const used = new Set<string>();
      const cells: [number, number][] = [[0, 0]];
      used.add("0,0");
      while (cells.length < 20) {
        const [ci, cj] = cells[Math.floor(r() * cells.length)];
        const d = [[1, 0], [-1, 0], [0, 1], [0, -1]][Math.floor(r() * 4)];
        const ni = ci + d[0];
        const nj = cj + d[1];
        if (Math.abs(ni) > 5 || Math.abs(nj) > 2 || used.has(`${ni},${nj}`)) continue;
        used.add(`${ni},${nj}`);
        cells.push([ni, nj]);
      }
      cells.sort((a, b2) => a[1] - b2[1]);
      const du = 0.05;
      const dv = 0.15;
      const water: RGB = mix([70, 140, 210], pal.mid, 0.2);
      for (const [i, j] of cells) {
        const u0 = 0.5 + (i - 0.5) * du;
        const v0 = 0.44 + (j - 0.5) * dv;
        quad(u0, v0, u0 + du, v0 + dv, shade(pal.dark, 0.6));
        const kind = Math.floor(r() * 4);
        const field = kind === 1 ? pal.light : pal.mid;
        quad(u0 + 0.004, v0 + 0.012, u0 + du - 0.004, v0 + dv - 0.012, field);
        if (kind === 0) quad(u0 + 0.004, v0 + dv * 0.42, u0 + du - 0.004, v0 + dv * 0.58, CREAM);
        if (kind === 2) quad(u0 + du * 0.42, v0 + 0.012, u0 + du * 0.58, v0 + dv - 0.012, CREAM);
        if (kind === 3) quad(u0 + 0.004, v0 + 0.012, u0 + du * 0.5, v0 + dv * 0.5, water);
        if (r() < 0.25) quad(u0 + du * 0.55, v0 + dv * 0.5, u0 + du - 0.004, v0 + dv - 0.012, pal.dark);
        nodes.push([u0 + du / 2, v0 + dv * 0.6]);
      }
      const [sx, sy] = P(0.2, 0.36);
      for (let k = 0; k < 6; k++) b.box(sx, sy - k, sx + 6, sy + 4 - k, k % 2 ? shade(pal.dark, 0.7) : shade(pal.dark, 0.9));
      b.box(sx, sy - 6, sx + 6, sy - 2, pal.dark);
      b.set(sx + 3, sy - 4, pal.light);
      break;
    }
    default:
      break;
  }
  const onBoard = (s: Bitmap) => {
    if (!nodes.length) return;
    const i = Math.floor(r() * nodes.length);
    const [s0, t0] = nodes[i];
    nodes.splice(i, 1);
    if (kit.surface === "tiles" || kit.surface === "mats") put(s, s0, t0);
    else putB(s, s0, t0);
  };

  // ---- cards
  const spots = hasBoard ? SPOTS.filter((sp) => sp.side !== "far") : SPOTS;
  if (kit.hands) for (const sp of spots) fan(sp.u, sp.v, sp.side, sp.side === "near");
  if (kit.tableau && !hasBoard) for (const sp of SPOTS) {
    const [u, v] = towards(sp, sp.side === "far" ? 0.24 : 0.22);
    const [x, y] = P(u, v);
    const n = sp.side === "left" || sp.side === "right" ? Math.min(3, kit.tableau) : kit.tableau;
    for (let k = 0; k < n; k++) {
      if (sp.side === "left" || sp.side === "right") card(x - 3, y - n * 4 + k * 7, true, PC[(k + Math.floor(r() * 6)) % 6]);
      else card(x - n * 4 + k * 8, y - 2, true, PC[(k + Math.floor(r() * 6)) % 6]);
    }
  }
  if (kit.roles) {
    // a round tracker in the middle, a secret role card in front of everyone
    quad(0.36, 0.36, 0.64, 0.56, INK);
    quad(0.365, 0.38, 0.635, 0.54, mix(pal.dark, pal.mid, 0.35));
    for (let k = 0; k < 5; k++) {
      const [x, y] = P(0.4 + k * 0.05, 0.46);
      b.box(x - 1, y - 1, x + 1, y + 1, k < 2 ? (k ? [214, 70, 60] : [70, 170, 90]) : pal.light);
    }
    for (const sp of SPOTS) {
      const [u, v] = towards(sp, 0.18);
      const [x, y] = P(u, v);
      bigCard(x - 4, y - 3, false);
    }
  }
  if (kit.decks) for (const sp of SPOTS.slice(0, 4)) {
    const [u, v] = towards(sp, 0.22);
    const [x, y] = P(u + (sp.u < 0.5 ? -0.06 : 0.06), v);
    deck(x - 4, y - 2, 2);
    card(x + 4, y - 2, true);
  }
  if (kit.market) {
    const n = kit.market;
    const [x0, y0] = P(0.5, 0.4);
    const w = n * 10;
    if (kit.deck) deck(x0 - Math.floor(w / 2) - 10, y0, 4);
    for (let k = 0; k < n; k++) bigCard(x0 - Math.floor(w / 2) + k * 10, y0, true, PC[(k * 5 + Math.floor(r() * 6)) % 6]);
  } else if (kit.deck && !hasBoard) {
    const [x0, y0] = P(0.44, 0.4);
    deck(x0 - 4, y0, 5);
    card(x0 + 6, y0 + 1, true);
  } else if (kit.deck) {
    const [x0, y0] = P(0.9, 0.74);
    deck(x0 - 3, y0, 4);
  }
  if (kit.trick) {
    const [x, y] = P(0.5, 0.45);
    card(x - 3, y - 8, true, PC[0]);
    card(x - 11, y - 3, true, [40, 40, 50]);
    card(x + 5, y - 3, true, PC[0]);
    card(x - 3, y + 2, true, [40, 40, 50]);
  }
  if (kit.story) {
    const [x0, y0] = P(0.5, 0.36);
    for (let k = 0; k < 5; k++) {
      const x = x0 - 25 + k * 10;
      b.box(x, y0, x + 8, y0 + 8, CREAM, INK);
      b.box(x + 2, y0 + 2, x + 6, y0 + 6, mix(pal.light, pal.mid, (k % 3) / 2));
      b.ellipse(x + 2 + Math.floor(r() * 2), y0 + 3, x + 4 + Math.floor(r() * 2), y0 + 5, k % 2 ? pal.dark : PC[k % 6]);
    }
  }
  if (kit.sheets) for (const sp of SPOTS.slice(0, kit.surface === "none" ? 6 : 4)) {
    if (hasBoard && sp.side === "far") continue;
    const [u, v] = towards(sp, sp.side === "far" ? 0.14 : 0.12);
    const [x, y] = P(u, v);
    b.box(x - 6, y - 3, x + 6, y + 4, [250, 250, 244], [120, 120, 130]);
    for (let k = 0; k < 3; k++) for (let c = 0; c < 5; c++) b.set(x - 4 + c * 2, y - 1 + k * 2, [150, 164, 210]);
    for (let k = 0; k < 4; k++) b.set(x - 4 + Math.floor(r() * 5) * 2, y - 1 + Math.floor(r() * 3) * 2, [210, 50, 50]);
    b.line(x + 7, y + 4, x + 11, y + 1, [240, 196, 60]);
    b.set(x + 7, y + 4, INK);
  }
  if (kit.boards) for (const sp of SPOTS) {
    if (hasBoard && sp.side === "far") continue;
    const [u, v] = towards(sp, sp.side === "far" ? 0.14 : 0.08);
    const [x, y] = P(u, v);
    b.box(x - 5, y - 2, x + 5, y + 2, shade(pal.light, 0.9));
    b.line(x - 5, y + 3, x + 5, y + 3, shade(pal.dark, 0.6));
    for (let k = 0; k < 3; k++) b.box(x - 4 + k * 3, y - 1, x - 3 + k * 3, y, shade(pal.mid, 0.8));
  }
  if (kit.pads) {
    const [x, y] = P(0.9, 0.78);
    b.box(x - 4, y - 2, x + 3, y + 4, [250, 248, 236]);
    for (let k = 0; k < 3; k++) b.line(x - 3, y + k * 2, x + 2, y + k * 2, [150, 160, 200]);
    b.line(x - 2, y + 5, x + 4, y + 2, [240, 196, 60]);
  }
  if (kit.tome) {
    const [x, y] = P(0.06, 0.74);
    b.box(x - 9, y - 3, x + 9, y + 4, shade(pal.mid, 0.6));
    b.box(x - 8, y - 4, x - 1, y + 3, CREAM);
    b.box(x + 1, y - 4, x + 8, y + 3, CREAM);
    b.line(x, y - 4, x, y + 3, [180, 170, 150]);
    for (let k = 0; k < 3; k++) {
      b.line(x - 7, y - 2 + k * 2, x - 2, y - 2 + k * 2, [130, 120, 120]);
      b.line(x + 2, y - 2 + k * 2, x + 7, y - 2 + k * 2, [130, 120, 120]);
    }
    b.box(x + 3, y - 3, x + 6, y - 2, pal.dark);
  }
  if (kit.tablet) {
    const [x, y] = P(kit.tome ? 0.9 : 0.06, 0.74);
    b.box(x - 8, y - 4, x + 8, y + 4, [30, 30, 36]);
    b.box(x - 7, y - 3, x + 7, y + 3, mix([60, 150, 220], pal.mid, 0.3));
    b.box(x - 5, y - 2, x - 1, y + 1, pal.light);
    b.line(x + 1, y + 1, x + 5, y - 1, [250, 220, 100]);
    scene.em.box(x - 7, y - 3, x + 7, y + 3, mix([40, 110, 180], pal.mid, 0.2));
  }
  if (kit.glasses) {
    const [x, y] = P(0.66, 0.92);
    b.box(x - 4, y - 1, x + 4, y + 1, [40, 40, 48]);
    b.box(x - 3, y - 1, x + 3, y, [90, 200, 240]);
  }
  if (kit.coins) for (const sp of SPOTS) {
    if (hasBoard && sp.side === "far") continue;
    const [u, v] = towards(sp, sp.side === "far" ? 0.12 : 0.09);
    const [x, y] = P(u + (sp.u < 0.5 ? 0.04 : -0.04), v);
    const n = 2 + Math.floor(r() * 4);
    for (let k = 0; k < n; k++) {
      b.line(x - 1, y - k, x + 1, y - k, k % 2 ? [214, 170, 60] : [250, 214, 100]);
    }
    b.box(x + 3, y - 1, x + 7, y + 1, mix([110, 170, 110], pal.light, 0.2));
  }
  if (kit.tape) {
    const [x0, y0] = P(bu(0.35), bv(0.2));
    const [x1, y1] = P(bu(0.52), bv(0.85));
    b.line(x0, y0, x1, y1, [250, 210, 60]);
    for (let k = 0; k <= 5; k++) b.set(Math.round(x0 + ((x1 - x0) * k) / 5) + 1, Math.round(y0 + ((y1 - y0) * k) / 5), INK);
  }
  if (e >= 3 || (year >= 2008 && r() < 0.6)) {
    const [x, y] = P(0.5, 0.92);
    b.box(x - 1, y - 2, x + 2, y + 2, [24, 24, 30]);
    b.box(x, y - 1, x + 1, y + 1, [70, 90, 130]);
  }
  if (kit.tokens) {
    const [cx, cy] = P(kit.story ? 0.5 : 0.62, kit.story ? 0.66 : 0.55);
    for (let k = 0; k < kit.tokens; k++) {
      const x = cx - 8 + Math.floor(r() * 17);
      const y = cy - 2 + Math.floor(r() * 5);
      const c = kit.roles ? (k % 2 ? [70, 170, 90] : [214, 70, 60]) as RGB : k % 3 ? pal.mid : pal.light;
      b.box(x - 1, y, x + 1, y + 1, c);
      b.line(x - 1, y, x + 1, y, shade(c, 1.3));
    }
  }

  // ---- standing pieces
  const ppl = Math.min(4, kit.pawns ?? 0);
  for (let i = 0; i < ppl; i++) onBoard(pawnSpr(PC[i % PC.length]));
  if (kit.pawns && kit.pawns > 4) for (let i = 4; i < kit.pawns; i++) onBoard(pawnSpr(PC[i % PC.length]));
  for (let i = 0; i < (kit.meeples ?? 0); i++) onBoard(meepleSpr(PC[i % 4]));
  if (kit.resources) for (const [u, c] of [[0.11, [160, 104, 60]], [0.89, [150, 150, 156]], [0.13, [240, 200, 70]]] as [number, RGB][]) {
    for (let k = 0; k < 4; k++) put(cubeSpr(c), u + (r() - 0.5) * 0.05, (u === 0.13 ? 0.66 : 0.52) + (r() - 0.5) * 0.12);
  }
  for (let i = 0; i < (kit.cubes ?? 0); i++) {
    if (!nodes.length) break;
    const [s, t] = nodes[Math.floor(r() * nodes.length)];
    const c = PC[i % 4];
    const h = 1 + Math.floor(r() * 2);
    for (let k = 0; k < h; k++) {
      const sp = cubeSpr(c);
      const [x, y] = P(hasBoard ? bu(s) : s, hasBoard ? bv(t) : t);
      stand.push({ x: x - 2 + (i % 2) * 3, y: y - 3 - k * 2, s: sp });
    }
  }
  for (let i = 0; i < (kit.chits ?? 0); i++) onBoard(chitSpr(i % 2 ? mix(pal.light, CREAM, 0.3) : mix(PC[0], pal.mid, 0.3)));
  const minis = kit.minis ?? 0;
  for (let i = 0; i < minis; i++) {
    if (kit.surface === "terrain") {
      const sideA = i % 2 === 0;
      const s = 0.12 + (Math.floor(i / 2) / Math.max(1, Math.ceil(minis / 2))) * 0.76 + (r() - 0.5) * 0.04;
      putB(miniSpr(sideA ? pal.light : mix([214, 72, 60], pal.mid, 0.3)), s, sideA ? 0.2 + r() * 0.08 : 0.86 + r() * 0.08);
    } else if (hasBoard) onBoard(miniSpr(PC[i % 4]));
    else put(miniSpr(PC[i % 4]), 0.35 + r() * 0.3, 0.45 + r() * 0.2);
  }
  if (kit.monster) onBoard(monsterSpr(mix(pal.mid, [90, 170, 80], 0.3)));
  if (kit.surface === "grid" && !kit.pawns) for (let i = 0; i < (o.pieces ?? pieces); i++) onBoard(pawnSpr(PC[i % PC.length]));
  // dice: rolled in front of the board, or in the middle of a card table
  const dice = kit.dice ?? 0;
  const coloured = o.format === "dice" || o.mechanic === "dice_drafting" || o.mechanic === "roll_write";
  for (let i = 0; i < dice; i++) {
    const c = coloured ? PC[i % 6] : [244, 240, 230] as RGB;
    const pip = coloured ? [250, 250, 244] as RGB : INK;
    const u = hasBoard ? 0.36 + r() * 0.28 : 0.38 + r() * 0.24;
    const v = hasBoard ? 0.84 + r() * 0.06 : 0.48 + r() * 0.2;
    put(dieSpr(c, pip, Math.floor(r() * 4)), u, v);
  }
  for (let i = 0; i < (kit.poly ?? 0); i++) put(polySpr(PC[(i + 1) % 6]), 0.12 + r() * 0.06, 0.38 + r() * 0.2 + (kit.surface === "mats" ? 0 : 0.3));
  if (kit.cup) put(cupSpr(), 0.68, 0.84);
  if (kit.timer) put(timerSpr(), hasBoard || kit.surface === "tiles" ? 0.9 : 0.5, hasBoard || kit.surface === "tiles" ? 0.6 : 0.7);
  if (kit.tower) {
    put(towerSpr(r), 0.5, 0.52);
    for (let k = 0; k < 3; k++) {
      const [x, y] = P(0.4 + r() * 0.2, 0.62 + r() * 0.15);
      b.box(x - 2, y, x + 2, y + 1, [214, 172, 112]);
      b.line(x - 2, y + 1, x + 2, y + 1, [150, 110, 70]);
    }
  }
  if (kit.bag) put(bagSpr(mix(pal.mid, [140, 90, 60], 0.3)), 0.9, 0.62);
  if (kit.screen) {
    const sw = 17;
    const scr = new Bitmap(sw, 8);
    for (let k = 0; k < 3; k++) {
      const c = k === 1 ? pal.mid : shade(pal.mid, 0.75);
      scr.box(k * 6, k === 1 ? 0 : 1, k * 6 + 4, 7, c);
      scr.box(k * 6 + 1, k === 1 ? 1 : 2, k * 6 + 3, 3, pal.light);
    }
    put(scr.outlined(INK), kit.surface === "city" ? 0.9 : 0.1, 0.5);
  }
  stand.sort((a, c) => a.y + a.s.h - (c.y + c.s.h));
  for (const it of stand) b.blit(it.s, it.x, it.y);
  return b;
}


/** The design studio close-up: desks, a whiteboard full of ideas, and a prototype in progress. */
export function designCloseup(year: number, seed = 11): CloseupScene {
  const r = artRng(seed);
  const sc = new Bitmap(CW, CH);
  const em = new Bitmap(CW, CH);
  const fg = new Bitmap(CW, CH);
  backWall(sc, [84, 100, 132], r);
  windowAndCurtains(sc, em);
  // whiteboard with sketches
  sc.box(150, 18, 290, 80, [140, 140, 156]);
  sc.box(152, 20, 288, 78, [246, 246, 240]);
  for (let k = 0; k < 5; k++) sc.strokeBox(160 + k * 24, 28, 176 + k * 24, 44, [110, 160, 220]);
  for (let k = 0; k < 4; k++) sc.line(160, 52 + k * 6, 160 + Math.floor(r() * 110), 52 + k * 6, [[220, 80, 80], [70, 70, 90], [60, 150, 90], [70, 70, 90]][k] as unknown as RGB);
  drawText(sc, 230, 30, String(year), [220, 80, 80], 0);
  // corkboard and bookshelf
  sc.box(300, 24, 352, 70, [120, 80, 50]);
  sc.box(302, 26, 350, 68, [186, 138, 88]);
  for (let k = 0; k < 5; k++) sc.box(306 + k * 9, 30 + (k % 2) * 16, 313 + k * 9, 40 + (k % 2) * 16, [[244, 240, 224], [250, 222, 120], [160, 210, 236]][k % 3] as unknown as RGB);
  sc.box(370, 9, 462, 100, [56, 38, 32]);
  for (const base of [38, 68, 98]) {
    sc.box(370, base, 462, base + 2, [120, 74, 46]);
    let x = 374;
    while (x < 456) {
      const bw = 4 + Math.floor(r() * 4);
      const bh = 14 + Math.floor(r() * 10);
      sc.box(x, base - bh, x + bw, base - 1, BOXC[Math.floor(r() * BOXC.length)]);
      x += bw + 1;
    }
  }
  perspectiveFloor(sc, r, [[78, 94, 132], [72, 88, 124], [84, 100, 138], [70, 84, 118]]);
  bigPlant(sc);
  // two desks with monitors and lamps
  for (const [dx, dy] of [[150, 150], [300, 150]]) {
    sc.box(dx - 40, dy, dx + 40, dy + 6, [176, 124, 80]);
    sc.box(dx - 40, dy + 7, dx + 40, dy + 10, [140, 96, 60]);
    for (const lx of [dx - 38, dx + 34]) sc.box(lx, dy + 10, lx + 4, dy + 40, [120, 80, 50]);
    sc.box(dx - 14, dy - 22, dx + 14, dy - 2, [40, 44, 56]);
    sc.box(dx - 12, dy - 20, dx + 12, dy - 4, [150, 220, 250]);
    em.box(dx - 12, dy - 20, dx + 12, dy - 4, [150, 220, 250]);
    sc.box(dx - 2, dy - 2, dx + 2, dy, [60, 64, 74]);
    for (let k = 0; k < 4; k++) sc.box(dx + 16 + k * 5, dy + 1, dx + 19 + k * 5, dy + 4, BOXC[k]);
    sc.line(dx - 30, dy, dx - 30, dy - 18, [60, 60, 70]);
    sc.box(dx - 34, dy - 22, dx - 26, dy - 18, [46, 112, 84]);
    em.box(dx - 33, dy - 18, dx - 27, dy - 17, [255, 236, 170]);
  }
  for (const [x0, y0, x1, y1, w, c] of [[-10, 280, 60, 196, 16, [30, 70, 46]], [20, 290, 110, 222, 14, [36, 82, 52]]] as [number, number, number, number, number, RGB][]) leaf(fg, x0, y0, x1, y1, w, c, shade(c, 1.4));
  return {
    kind: "design", img: sc, em, fg,
    seats: [
      { x: 137, y: 148, view: "back", hx: 150, hy: 150 },
      { x: 287, y: 148, view: "back", hx: 300, hy: 150 },
      { x: 212, y: 160, view: "back", hx: 225, hy: 160 },
    ],
    designer: { x: 60, y: 160, view: "front", hx: 73, hy: 160 },
    lights: [
      { px: 120, py: 140, color: [1.0, 0.82, 0.5], radius: 70, strength: 1.0 },
      { px: 270, py: 140, color: [1.0, 0.82, 0.5], radius: 70, strength: 1.0 },
      { px: 240, py: 200, color: [0.6, 0.55, 0.62], radius: 260, strength: 0.8 },
      { px: 75, py: 50, color: [0.62, 0.42, 0.34], radius: 95, strength: 1.0 },
    ],
    shafts: [{ pts: [[40, 22], [108, 22], [250, 270], [112, 270]] }],
    cat: { x: 430, y: 220 },
  };
}

/** Shop-floor tones and signage by decade: a toy shop in the 60s and 70s, a hobby shop later, a games café from the 2000s. */
function shopLook(year: number): { wall: RGB; floor: RGB[]; sign: string; signC: RGB } {
  if (year < 1980) return { wall: [120, 70, 64], floor: [[122, 84, 58], [114, 78, 54], [130, 90, 62], [108, 74, 50]], sign: "TOYS & GAMES", signC: [250, 214, 120] };
  if (year < 2000) return { wall: [62, 74, 110], floor: [[96, 92, 104], [90, 86, 98], [104, 98, 110], [84, 80, 92]], sign: "GAME SHOP", signC: [140, 230, 250] };
  return { wall: [58, 88, 78], floor: [[124, 96, 70], [116, 90, 66], [132, 102, 74], [110, 84, 62]], sign: "GAMES CAFE", signC: [255, 190, 120] };
}

/**
 * Launch day at the shop: shelves of games, a NEW TODAY banner, the new box on a lit plinth on the left,
 * and a crowd that grows with the first week's sales (the caller seats as many as it likes, front to back).
 */
export function launchCloseup(year: number, seed = 5): CloseupScene {
  const r = artRng(seed + year);
  const look = shopLook(year);
  const sc = new Bitmap(CW, CH);
  const em = new Bitmap(CW, CH);
  const fg = new Bitmap(CW, CH);
  backWall(sc, look.wall, r);
  windowAndCurtains(sc, em);
  // shelves of other people's games, wall to wall on the right
  gameShelf(sc, r);
  sc.box(150, 9, 290, 100, [56, 38, 32]);
  for (const base of [38, 68, 98]) {
    sc.box(150, base, 290, base + 2, [120, 74, 46]);
    sc.line(150, base, 290, base, [156, 102, 64]);
    let x = 154;
    while (x < 284) {
      const bw = 8 + Math.floor(r() * 8);
      const bh = 14 + Math.floor(r() * 12);
      const c = BOXC[Math.floor(r() * BOXC.length)];
      sc.box(x, base - bh, x + bw, base - 1, c);
      sc.line(x, base - bh, x + bw, base - bh, shade(c, 1.35));
      x += bw + 2;
    }
  }
  // the shop sign over the window, and a NEW TODAY banner strung across
  sc.box(30, 2, 118, 9, [40, 26, 22]);
  drawText(sc, 36, 3, look.sign, look.signC, 0);
  drawText(em, 36, 3, look.sign, look.signC, 0);
  sc.line(4, 104, 236, 100, [70, 50, 40]);
  for (let k = 0; k < 11; k++) {
    const x = 10 + k * 21;
    const y = 104 - Math.round((k * 4) / 11);
    const c: RGB = [[232, 88, 72], [34, 150, 140], [222, 164, 64], [90, 140, 220]][k % 4] as unknown as RGB;
    sc.poly([[x, y], [x + 12, y], [x + 6, y + 10]], c);
  }
  sc.box(150, 104, 236, 114, [232, 88, 72]);
  sc.line(150, 104, 236, 104, [255, 140, 120]);
  drawText(sc, 160, 106, "NEW TODAY", [255, 246, 226], 0);
  drawText(em, 160, 106, "NEW TODAY", [255, 200, 180], 0);
  perspectiveFloor(sc, r, look.floor);
  // the till on the right
  sc.box(360, 150, 470, 176, [140, 92, 58]);
  sc.box(360, 146, 470, 151, [176, 124, 80]);
  sc.box(420, 128, 446, 146, [60, 64, 74]);
  sc.box(423, 131, 443, 139, [150, 220, 160]);
  em.box(423, 131, 443, 139, [150, 220, 160]);
  for (let k = 0; k < 4; k++) sc.box(372 + k * 10, 138 - k * 2, 380 + k * 10, 146, BOXC[(k + 2) % BOXC.length]);
  // the plinth the new box stands on, under a spotlight
  // the plinth and the crowd sit in the left half: on a wide screen the reviews panel covers the right
  const px = 26;
  const py = 200;
  sc.ellipse(px - 10, py + 26, px + 70, py + 40, [40, 28, 24], 140);
  sc.box(px, py, px + 60, py + 30, [70, 52, 64]);
  sc.box(px - 4, py - 4, px + 64, py + 1, [214, 182, 120]);
  sc.line(px - 4, py - 4, px + 64, py - 4, [250, 226, 170]);
  sc.box(px + 4, py + 6, px + 56, py + 26, [92, 70, 84]);
  drawText(sc, px + 12, py + 12, "NEW", [250, 226, 170], 0);
  // velvet rope posts either side
  for (const rx of [px - 14, px + 70]) {
    sc.box(rx, py + 2, rx + 3, py + 30, [206, 164, 84]);
    sc.ellipse(rx - 2, py - 2, rx + 5, py + 4, [236, 196, 110]);
  }
  sc.line(px - 11, py + 8, px - 4, py + 14, [170, 40, 56]);
  sc.line(px + 64, py + 14, px + 70, py + 8, [170, 40, 56]);
  // a few balloons in the foreground corner
  for (const [bx, by, c] of [[452, 30, [232, 88, 72]], [466, 46, [34, 150, 140]], [440, 52, [222, 164, 64]]] as [number, number, RGB][]) {
    fg.line(bx, by + 10, bx - 6, by + 70, [230, 230, 230]);
    fg.ellipse(bx - 8, by - 10, bx + 8, by + 10, c);
    fg.ellipse(bx - 5, by - 7, bx - 1, by - 2, shade(c, 1.5));
  }
  // front-facing seats: the crowd queues behind the rope, nearest first, then spreading right towards the till
  const seats: CloseupSeat[] = [];
  const spots: [number, number][] = [[118, 152], [144, 134], [170, 156], [130, 116], [156, 110], [178, 138], [198, 152], [212, 120], [232, 142], [250, 110], [270, 152], [290, 126]];
  for (const [x, y] of spots) seats.push({ x, y, view: "front", hx: x + 13, hy: y });
  return {
    kind: "launch", img: sc, em, fg, seats,
    designer: { x: 100, y: 176, view: "front", hx: 113, hy: 176 },
    lights: [
      { px: px + 30, py: py - 20, color: [1.0, 0.86, 0.56], radius: 80, strength: 1.2 },
      { px: 240, py: 150, color: [0.9, 0.78, 0.62], radius: 220, strength: 0.7 },
      { px: 430, py: 130, color: [0.6, 0.9, 0.7], radius: 50, strength: 0.6 },
      { px: 75, py: 50, color: [0.62, 0.42, 0.34], radius: 95, strength: 1.0 },
    ],
    shafts: [{ pts: [[40, 22], [108, 22], [200, 270], [80, 270]] }],
    cat: { x: 440, y: 230 },
    display: { x: px - 6, y: py - 58, w: 72, h: 54 },
  };
}

/**
 * The 2035 awards night at Dicefall Con: a stage under spotlights with a podium and a trophy, curtains,
 * a banner, and rows of the audience seen from behind. Winners walk up to the podium (the designer spot).
 */
export function finaleCloseup(seed = 9): CloseupScene {
  const r = artRng(seed);
  const sc = new Bitmap(CW, CH);
  const em = new Bitmap(CW, CH);
  const fg = new Bitmap(CW, CH);
  // the hall: deep blue-violet with a faint pattern
  sc.rect(0, 0, CW, CH, [34, 28, 56]);
  for (let y = 6; y < 130; y += 10) for (let x = (y % 20) / 2; x < CW; x += 20) sc.set(x, y, [48, 40, 76]);
  // the stage platform across the left of the hall (the awards list covers the right on a wide screen)
  const sx0 = 0;
  const sx1 = 300;
  const top = 150;
  sc.box(sx0, top, sx1, top + 4, [196, 150, 92]);
  sc.line(sx0, top, sx1, top, [236, 196, 130]);
  sc.box(sx0, top + 5, sx1, top + 34, [92, 54, 40]);
  for (let x = sx0 + 6; x < sx1; x += 18) sc.line(x, top + 6, x, top + 33, [74, 42, 32]);
  // curtains: a red swag along the top and drapes at the side
  sc.rect(0, 0, CW, 14, [150, 34, 50]);
  for (let x = 0; x < CW; x += 24) sc.ellipse(x - 2, 6, x + 26, 22, [170, 44, 60]);
  for (let x = 0; x < CW; x += 24) sc.line(x + 12, 12, x + 12, 21, [120, 26, 40]);
  for (const x0 of [0, 286]) {
    sc.box(x0, 10, x0 + 18, top - 1, [140, 30, 46]);
    for (let fx = x0 + 3; fx < x0 + 18; fx += 4) sc.line(fx, 12, fx, top - 2, [110, 22, 36]);
    sc.line(x0 + 1, 12, x0 + 1, top - 2, [180, 60, 74]);
  }
  // the banner and its letters
  sc.box(44, 26, 256, 46, [232, 88, 72]);
  sc.line(44, 26, 256, 26, [255, 140, 120]);
  sc.line(44, 46, 256, 46, [150, 50, 40]);
  drawText(sc, 60, 30, "DICEFALL CON", [255, 246, 226], 0);
  drawText(em, 60, 30, "DICEFALL CON", [255, 200, 170], 0);
  drawText(sc, 196, 30, "2035", [255, 220, 120], 0);
  drawText(em, 196, 30, "2035", [255, 210, 120], 0);
  // a big screen behind the podium, showing a gold die
  sc.box(70, 56, 230, 136, [20, 20, 30]);
  sc.box(74, 60, 226, 132, [40, 60, 110]);
  em.box(74, 60, 226, 132, [10, 16, 34]);
  sc.box(182, 72, 210, 100, [236, 190, 80]);
  sc.strokeBox(182, 72, 210, 100, [160, 120, 50]);
  for (const [dx, dy] of [[189, 79], [203, 93], [196, 86]]) sc.box(dx - 1, dy - 1, dx + 1, dy + 1, [120, 80, 30]);
  em.box(182, 72, 210, 100, [200, 150, 60]);
  // the podium with the trophy on it
  sc.box(120, 118, 150, 150, [70, 52, 64]);
  sc.box(116, 114, 154, 119, [214, 182, 120]);
  sc.line(116, 114, 154, 114, [250, 226, 170]);
  sc.box(125, 126, 145, 140, [92, 70, 84]);
  sc.box(133, 104, 137, 113, [160, 120, 50]);
  sc.poly([[128, 92], [142, 92], [139, 103], [131, 103]], [236, 190, 80]);
  sc.line(130, 93, 132, 100, [255, 236, 160]);
  em.poly([[128, 92], [142, 92], [139, 103], [131, 103]], [180, 140, 50]);
  // the audience floor, darker towards the front
  for (let y = top + 35; y < CH; y++) sc.line(0, y, CW, y, mix([44, 34, 58], [24, 18, 34], (y - top - 35) / (CH - top - 35)));
  // rows of seats: chair backs the audience sits in
  const seats: CloseupSeat[] = [];
  const rows = [[204, 0], [236, 12]];
  for (const [y, off] of rows) for (let k = 0; k < 9; k++) {
    const x = 8 + off + k * 30;
    seats.push({ x, y, view: "back", hx: x + 13, hy: y });
  }
  // nearest row first, so the first few seats taken are the ones you can see
  seats.sort((a, b) => b.y - a.y || a.x - b.x);
  // stage lights hanging from the top, and their glow
  for (const lx of [40, 110, 190, 260]) {
    sc.box(lx - 4, 14, lx + 4, 22, [40, 40, 50]);
    em.box(lx - 2, 21, lx + 2, 23, [255, 236, 190]);
  }
  for (let i = 0; i < 60; i++) {
    const x = Math.floor(r() * CW);
    const y = Math.floor(r() * 140);
    if (x > 300) em.set(x, y, [60, 50, 90]);
  }
  void fg;
  return {
    kind: "finale", img: sc, em, fg, seats,
    designer: { x: 92, y: 102, view: "front", hx: 105, hy: 102 },
    lights: [
      { px: 112, py: 124, color: [1.0, 0.9, 0.62], radius: 60, strength: 0.55 },
      { px: 150, py: 96, color: [1.0, 0.82, 0.4], radius: 40, strength: 0.5 },
      { px: 150, py: 40, color: [1.0, 0.7, 0.6], radius: 120, strength: 0.7 },
      { px: 150, py: 230, color: [0.4, 0.34, 0.6], radius: 240, strength: 0.6 },
    ],
    shafts: [{ pts: [[100, 22], [124, 22], [150, 150], [70, 150]] }, { pts: [[176, 22], [200, 22], [210, 150], [140, 150]] }],
    cat: { x: 440, y: 240 },
  };
}
